import { useCallback } from "react";
import { useLang } from "../context/LanguageContext";

// Dashboard / app-shell strings. English keys double as nav labels for zero-diff wiring.
export const STRINGS = {
  en: {
    // Nav (keyed by English label)
    Dashboard: "Dashboard", Offers: "Offers", Talents: "Talents", Bookings: "Bookings",
    Messages: "Messages", Support: "Support", "Refer & earn": "Refer & earn",
    "Account & privacy": "Account & privacy", Gigs: "Gigs", "My Offers": "My Offers",
    Availability: "Availability", Credits: "Credits", Settings: "Settings", Roster: "Roster",
    "Sign out": "Sign out", credit: "credit", credits: "credits",
    // Extra EN-keyed nav strings so dt() returns identical text in EN mode
    // (rather than falling back to the raw key — makes debugging easier).
    Overview: "Overview", Notifications: "Notifications", Work: "Work", Money: "Money",
    Profile: "Profile", Account: "Account", "Offers received": "Offers received",
    "New request": "New request", "Profile editor": "Profile editor", Invoices: "Invoices",
    Customers: "Customers", Media: "Media", Payments: "Payments", Earnings: "Earnings",
    "Pricing & packages": "Pricing & packages", "Tax & reporting": "Tax & reporting",
    "Billing & payments": "Billing & payments", "Agency profile": "Agency profile",
    "Command center": "Command center", "To do": "To do", Reminders: "Reminders",
    "Trust & Safety": "Trust & Safety", Verification: "Verification", Disputes: "Disputes",
    Moderation: "Moderation", Reviews: "Reviews", Market: "Market", Leads: "Leads",
    Refunds: "Refunds", Invoicing: "Invoicing", "Platform invoices": "Platform invoices",
    Commissions: "Commissions", Pricing: "Pricing", Users: "Users", Invitations: "Invitations",
    Growth: "Growth", Spotlight: "Spotlight", "AI Insights": "AI Insights",
    "Email templates": "Email templates", "Email log": "Email log", Content: "Content",
    "Content blocks": "Content blocks", Translations: "Translations", "DAC7 & tax": "DAC7 & tax",
    "Audit log": "Audit log", "Data Integrity": "Data Integrity", "Data & Archive": "Data & Archive",
    Security: "Security", "Admin roles": "Admin roles", "Error reports": "Error reports",
    // Dashboard
    available_gigs: "Available gigs", unlocked: "Unlocked", active_offers: "Active offers",
    credit_balance: "Credit balance", bookings: "Bookings", requests: "Requests",
    offers_received: "Offers received", waiting: "Waiting", roster: "Roster",
    browse_gigs: "Browse gigs", new_request: "New request",
    welcome_back: "Welcome back. Here's your booking activity.",
    manage_roster: "Manage your roster and book gigs on their behalf.",
    // Gigs
    gigs_intro: "Real booking requests. Unlock with a credit to see contact details and send an offer.",
    unlock: "Unlock", send_offer: "Send offer", offer_sent: "Offer sent",
    unlocked_badge: "Unlocked", fully_matched: "Fully matched", buy_to_unlock: "Buy credits to unlock",
    slots_left_one: "1 spot left of 5", slots_left_many: "{n} spots left of 5", slots_full: "All 5 spots taken",
    // Empty states
    no_bookings: "No confirmed bookings",
    no_bookings_sub: "When a client accepts your offer, it appears here with their contact details.",
    download_contract: "Download contract",
    no_offers: "No offers sent", no_offers_sub: "Unlock a gig and send your first offer to see it here.",
    // Offers
    accept: "Accept", decline: "Decline", counter: "Counter offer",
    accept_counter: "Accept counter", client_countered_at: "Client countered at",
    status_sent: "Sent", status_countered: "Countered", status_accepted: "Accepted",
    status_declined: "Declined", status_expired: "Expired", status_withdrawn: "Withdrawn", status_pending: "Pending",
    status_pending_approval: "Pending approval", status_published: "Published", status_matching: "Matching", status_responded: "Responded",
    // Credit chip
    Buy: "Buy", top_up: "Top up", buy_credits: "Buy credits",
    // Offer flow — reason strings (used on both talent & client sides)
    "reason_not_available": "Talent not available",
    "reason_wrong_scope": "Wrong scope",
    "reason_price_too_low": "Price too low",
    "reason_other": "Other reason",
    // Talent dashboard
    profile_status_prefix: "Your profile is",
    profile_status_suffix: "Complete your profile and wait for admin approval to appear publicly and receive matched gigs.",
    complete_profile: "Complete your profile",
    complete_profile_sub_suffix: "% complete, reach 50% to unlock everything and get 3 free credits.",
    improve: "Improve", running_low: "Running low, top up to keep unlocking gigs.",
    credits_never_expire: "Credits never expire", find_events: "Find events to book",
    my_offers: "My offers", track_pitches: "Track your pitches",
    // Client dashboard
    recent_requests: "Recent requests", view_offers: "View offers",
    no_requests: "Don't go hunting", no_requests_sub: "Post what you need, sit back, and let the priced offers roll in.",
    post_request: "Post Your First Request", offers_label: "offers",
    talent_live_header: "Clients are waiting", talent_live_sub: "Check the live requests board and send a priced offer to secure your next gig.", talent_live_cta: "View live requests",
    // Agency
    manage_roster_card: "Manage roster",
    // Gigs
    filter: "Filter", clear_all: "Clear all", locked: "Locked",
    no_gigs_match: "No gigs match your filters", try_clearing: "Try clearing some filters.",
    no_gigs: "No gigs available", no_gigs_sub: "New matched requests will show up here. Check back soon.",
    unlock_1_credit: "Unlock (1 credit)",
    filter_gigs: "Filter gigs", interest: "Interest", all_interests: "All interests",
    country: "Country", all_countries: "All countries", city: "City", all_cities: "All cities",
    reset: "Reset", show: "Show", gigs_lower: "gigs",
    // Client settings
    profile_tab: "Profile", notifications_tab: "Notifications", privacy_tab: "Privacy", account_tab: "Account",
    profile_photo: "Profile photo", full_name: "Full name", phone_number: "Phone number", city_label: "City",
    account_type_label: "Account type", private_label: "Private", corporate_label: "Corporate",
    company_name: "Company name", org_number: "Organisation number", contact_person: "Contact person",
    billing_email: "Billing email", billing_email_hint: "Invoices sent here", billing_information: "Billing information",
    save: "Save", save_prefs: "Save preferences",
    notif_new_offers: "New offers on my requests", notif_booking_conf: "Booking confirmations",
    notif_messages: "Messages", notif_platform_news: "Platform news",
    cookie_prefs: "Cookie preferences", essential_cookies: "Essential", essential_always_on: "Always on",
    analytics_cookies: "Analytics", marketing_cookies: "Marketing",
    your_data: "Your data", request_data: "Request your data", request_data_desc: "We'll email a copy of your data to you.",
    request_deletion: "Request account deletion",
    email_label: "Email", change_email: "Change email", change_email_prompt: "Enter your new email address",
    change_password: "Change password",
    delete_account: "Delete account", delete_account_desc: "This permanently removes your account and data. This cannot be undone.",
    delete_type_confirm: "Type DELETE to confirm", confirm_delete: "Delete my account", cancel: "Cancel",
    your_full_name: "Your full name", settings_title: "Account settings",
    ref_hero_title: "Invite talents, earn {n} credits each",
    ref_hero_body: "Share your link or send a personal email invite. When someone joins as a talent and confirms their email, you get {n} credits.",
    // Offer modal (payment term values are stored in Swedish; translate for display)
    "Betalning på plats": "Payment on site", "10 dagar netto": "Net 10 days",
    "20 dagar netto": "Net 20 days", "30 dagar netto": "Net 30 days",
    // Self-billing consent + invoice preview (SV/EN)
    "Review & send": "Review & send",
    "Review & accept": "Review & accept",
    "Accept the self-billing mandate to enable invoicing": "Accept the self-billing mandate to enable invoicing",
    "Talentsio issues invoices in your name. You review and approve every invoice before it is sent.":
      "Talentsio issues invoices in your name. You review and approve every invoice before it is sent.",
    "This is exactly what your customer will receive. Approve to send it.":
      "This is exactly what your customer will receive. Approve to send it.",
    "Loading preview…": "Loading preview…",
    "Could not load preview.": "Could not load preview.",
    "Please accept the self-billing mandate first.": "Please accept the self-billing mandate first.",
    "Approved & sent by talent": "Approved & sent by talent",
    // Talent dashboard (English is the primary language — SV override below).
    Home: "Home", Book: "Book",
    "Good night": "Good night", "Good morning": "Good morning", "Good day": "Good day",
    "Good afternoon": "Good afternoon", "Good evening": "Good evening",
    "Could not load the overview. Please reload the page.": "Could not load the overview. Please reload the page.",
    "Today's focus": "Today's focus",
    "A payout did not arrive": "A payout did not arrive",
    "The money is still with Stripe and is not lost. Check your bank details.": "The money is still with Stripe and is not lost. Check your bank details.",
    "Open Earnings": "Open Earnings",
    "counter-offers waiting for your reply": "counter-offers waiting for your reply",
    "The client has proposed a different price or set-up.": "The client has proposed a different price or set-up.",
    "Open offers": "Open offers",
    "gig matches your profile today": "gig matches your profile today",
    "gigs match your profile today": "gigs match your profile today",
    "Be quick — talents who reply first usually win the booking.": "Be quick — talents who reply first usually win the booking.",
    "View gigs": "View gigs",
    "No new gigs right now": "No new gigs right now",
    "We will let you know as soon as an event in your category appears.": "We will let you know as soon as an event in your category appears.",
    "Update profile": "Update profile",
    "Revenue last 30d": "Revenue last 30d",
    "vs previous": "vs previous",
    "New measurement": "New measurement",
    "Confirmed bookings": "Confirmed bookings",
    "last 30d": "last 30d",
    "Accept rate (90d)": "Accept rate (90d)",
    "Good position": "Good position",
    "Room to respond more": "Room to respond more",
    "Average rating": "Average rating",
    reviews_lc: "reviews",
    "No reviews yet": "No reviews yet",
    "Upcoming booking": "Upcoming booking",
    "All bookings": "All bookings",
    guests_lc: "guests", days_lc: "days",
    Today: "Today", Tomorrow: "Tomorrow", In: "In",
    "item left in your checklist": "item left in your checklist",
    "items left in your checklist": "items left in your checklist",
    "No upcoming bookings yet.": "No upcoming bookings yet.",
    "See available gigs": "See available gigs",
    Payout: "Payout", incoming: "incoming",
    "active offers": "active offers",
    Unlocks: "Unlocks", "gigs unlocked": "gigs unlocked",
    "credits left": "credits left",
    "Recent activity": "Recent activity",
    "Nothing new right now.": "Nothing new right now.",
    "Browse gigs": "Browse gigs",
    "Update calendar": "Update calendar",
    "Upload media": "Upload media",
    "Contact support": "Contact support",
    "Quick actions": "Quick actions",
    "Profile health": "Profile health",
    "Profile complete — well done.": "Profile complete — well done.",
    "Almost there. Fill in the rest to appear in more searches.": "Almost there. Fill in the rest to appear in more searches.",
    "Complete your profile to start receiving gigs.": "Complete your profile to start receiving gigs.",
    "Complete profile": "Complete profile",
    "You need credits to unlock gigs.": "You need credits to unlock gigs.",
    "Enough to unlock more gigs.": "Enough to unlock more gigs.",
    "Buy more": "Buy more",
    // Notifications page.
    Notifications: "Notifications",
    "Requires action": "Requires action",
    "Could not load notifications.": "Could not load notifications.",
    "Everything is marked as read.": "Everything is marked as read.",
    "Could not mark as read.": "Could not mark as read.",
    "item needs your attention.": "item needs your attention.",
    "items need your attention.": "items need your attention.",
    "Everything is read.": "Everything is read.",
    unread_lc: "unread",
    "Mark all as read": "Mark all as read",
    Unread: "Unread",
    "This week": "This week",
    All: "All",
    "Nothing needs your attention.": "Nothing needs your attention.",
    "No notifications here.": "No notifications here.",
    "We will get in touch when something happens with your bookings.": "We will get in touch when something happens with your bookings.",
    "This list only shows things that stay stuck if you do nothing: unanswered counter-offers, bounced payouts, overdue invoices and missing tax details.": "This list only shows things that stay stuck if you do nothing: unanswered counter-offers, bounced payouts, overdue invoices and missing tax details.",
    "A notification is marked as read when you open it. The bell in the top bar only shows unread items.": "A notification is marked as read when you open it. The bell in the top bar only shows unread items.",
    // Talent gigs page.
    "Open gigs": "Open gigs",
    "Refreshing…": "Refreshing…",
    "Release to refresh": "Release to refresh",
    "Pull to refresh": "Pull to refresh",
    "Updated just now": "Updated just now",
    "open in your match": "open in your match",
    credits_lc: "credits", credit_lc: "credit",
    "Buy credits": "Buy credits",
    Filter: "Filter",
    "All open": "All open", Alerts: "Alerts", Unlocked: "Unlocked", Hidden: "Hidden",
    Sort: "Sort",
    "Best match": "Best match", Newest: "Newest",
    "Highest budget": "Highest budget", "Soonest date": "Soonest date",
    "Matches your specialty": "Matches your specialty",
    "Matches your category": "Matches your category",
    Unlocked_short: "Unlocked",
    "Fully booked": "Fully booked",
    "Budget unknown": "Budget unknown",
    Budget: "Budget",
    "verified client": "verified client",
    unlocked_lc: "unlocked",
    "you have unlocked": "you have unlocked",
    Unlock: "Unlock",
    "View detail": "View detail",
    Hide: "Hide",
    // Credits page — English identity map (EN mode uses these values as-is;
    // SV mode overrides them below). Currency abbreviation is treated as a
    // translation so no locale-specific word is ever hardcoded in JSX.
    SEK: "SEK",
    // `currency.SEK` is the SSOT for the currency suffix used by
    // `lib/useMoneyFmt.js`. Any string like "1 250 kr" now comes from a
    // hook that reads this key, not from an inline `kr` literal.
    "currency.SEK": "SEK",
    "Your credits": "Your credits",
    "incl. VAT": "incl. VAT",
    "per credit": "per credit",
    "kr per credit": "SEK per credit",
    "Save %": "Save {n}%",
    "Could not save settings": "Could not save settings",
    "View your credit history": "View your credit history",
    "How much could you earn?": "How much could you earn?",
    "Most popular": "Most popular",
    "Best value": "Best value",
    "calc_sentence": "If your average gig pays {avg} and you unlock {leads} leads to win {gigs} gig{plural}, your cost is {cost}, just {pct}% of revenue",
    // Spotlight index page
    "TALENTSIO SPOTLIGHT": "TALENTSIO SPOTLIGHT",
    "Ideas, trends and guides for people who book talent": "Ideas, trends and guides for people who book talent",
    "Hand-picked articles for organisers, wedding couples and companies who want to book the right entertainment in Sweden.": "Hand-picked articles for organisers, wedding couples and companies who want to book the right entertainment in Sweden.",
    "No articles yet": "No articles yet",
    "We're working on new stories - check back soon.": "We're working on new stories - check back soon.",
    "Read article": "Read article",
    // Public Join page
    "Become a talent": "Become a talent",
    "Get booked by organisers across Sweden": "Get booked by organisers across Sweden",
    "Create your free profile in under two minutes and start receiving matched requests.": "Create your free profile in under two minutes and start receiving matched requests.",
    "3 free credits to get started": "3 free credits to get started",
    "Real booking requests with date and budget": "Real booking requests with date and budget",
    "Credits never expire, keep 100% of your fee": "Credits never expire, keep 100% of your fee",
    "join_pricing_line": "Start with 3 free credits, then unlock leads from just {per} {currency} each",
    "Create your free profile": "Create your free profile",
    "No card needed. No monthly fees.": "No card needed. No monthly fees.",
    "Email address": "Email address",
    // BookNowButton
    "Book now": "Book now",
    // BookingDetail page title fallback
    Booking: "Booking",
    // SpotlightArticle
    "Article not found": "Article not found",
    "Perhaps it was unpublished or moved.": "Perhaps it was unpublished or moved.",
    "Back to Spotlight": "Back to Spotlight",
    "Link copied": "Link copied",
    "All articles": "All articles",
    Share: "Share",
    "Book the right entertainment on Talentsio": "Book the right entertainment on Talentsio",
    "Post a free request and get priced offers from verified artists directly.": "Post a free request and get priced offers from verified artists directly.",
    "Post request": "Post request",
    // ForTalents public page
    "3 free credits when you complete your profile": "3 free credits when you complete your profile",
    "Full VAT invoice on every purchase": "Full VAT invoice on every purchase",
    "Welcome bonus:": "Welcome bonus:",
    "3 free unlocks worth 297 kr when you complete your profile.": "3 free unlocks worth 297 kr when you complete your profile.",
    "Simple, transparent pricing": "Simple, transparent pricing",
    "One flat price per unlock. No packages, no monthly fees. Keep what you invoice, minus a transparent 10% platform fee shown in every offer.": "One flat price per unlock. No packages, no monthly fees. Keep what you invoice, minus a transparent 10% platform fee shown in every offer.",
    "Per unlock": "Per unlock",
    "Same price for talents and agencies. Incl. VAT.": "Same price for talents and agencies. Incl. VAT.",
    "Take a 10-pack for 799 kr and save nearly 20%.": "Take a 10-pack for 799 kr and save nearly 20%.",
    // ClientInvoices — panel + STATE labels
    Unpaid: "Unpaid",
    "Partially paid": "Partially paid",
    Credited: "Credited",
    "To pay": "To pay",
    "Pay against invoice": "Pay against invoice",
    "Nothing to pay": "Nothing to pay",
    "Copy {label}": "Copy {label}",
    "billed at their respective VAT rates.": "billed at their respective VAT rates.",
    // Additional keys used by talent + client pages that the extended
    // keyword scanner surfaced. Kept English identity here so `dt()` in
    // EN mode returns the same visible text; the SV block overrides below.
    "Could not save.": "Could not save.",
    "Could not save": "Could not save",
    "Could not save card": "The card could not be saved.",
    "Could not remove card": "The card could not be removed.",
    "Send offer": "Send offer",
    "View public profile": "View public profile",
    "No price shown": "No price shown",
    Remove: "Remove",
    "Remove {label}?": "Remove {label}?",
    "Pay invoice": "Pay invoice",
    "Subtotal ex. VAT": "Subtotal ex. VAT",
    "Publish profile": "Publish profile",
  },
  sv: {
    // Nav — sidebar labels & section headers (kept in sync with DashboardLayout).
    Dashboard: "Översikt", Offers: "Offerter", Talents: "Talanger", Bookings: "Bokningar",
    Messages: "Meddelanden", Support: "Support", "Refer & earn": "Bjud in och tjäna",
    "Account & privacy": "Konto och integritet", Gigs: "Uppdrag", "My Offers": "Mina offerter",
    Availability: "Tillgänglighet", Credits: "Krediter", Settings: "Inställningar", Roster: "Lista",
    "Sign out": "Logga ut", credit: "kredit", credits: "krediter",
    // New EN-keyed nav strings (extracted from hardcoded Swedish).
    Overview: "Översikt", Notifications: "Notiser", Work: "Arbete", Money: "Pengar", Profile: "Profil",
    Account: "Konto", "Offers received": "Mottagna offerter", "New request": "Ny förfrågan",
    "Profile editor": "Profil-editor", "Invoices": "Fakturor", "Customers": "Kunder", "Media": "Media",
    "Payments": "Utbetalningar", "Earnings": "Intäkter", "Pricing & packages": "Priser & paket",
    "Tax & reporting": "Skatt & rapportering", "Billing & payments": "Betalning och fakturering",
    "Agency profile": "Byrå-profil",
    // Admin sidebar (extracted from hardcoded Swedish).
    "Command center": "Command center", "To do": "Att göra", "Reminders": "Påminnelser",
    "Trust & Safety": "Trust & Safety", "Verification": "Verifiering", "Disputes": "Tvister",
    "Moderation": "Moderation", "Reviews": "Recensioner", "Market": "Marknad", "Leads": "Leads",
    "Refunds": "Återbetalningar", "Invoicing": "Fakturering", "Platform invoices": "Plattformens fakturor",
    "Commissions": "Provisioner", "Pricing": "Priser", "Users": "Användare", "Invitations": "Inbjudningar",
    "Growth": "Tillväxt", "Spotlight": "Spotlight", "AI Insights": "AI-insikter",
    "Email templates": "E-postmallar", "Email log": "E-postlogg", "Content": "Innehåll",
    "Content blocks": "Innehållsblock", "Translations": "Översättningar",
    "DAC7 & tax": "DAC7 & skatt", "Audit log": "Revisionslogg", "Data Integrity": "Dataintegritet",
    "Data & Archive": "Data & arkiv", "Security": "Säkerhet", "Admin roles": "Adminroller",
    "Error reports": "Felrapporter",
    // Dashboard
    available_gigs: "Tillgängliga uppdrag", unlocked: "Upplåsta", active_offers: "Aktiva offerter",
    credit_balance: "Kreditsaldo", bookings: "Bokningar", requests: "Förfrågningar",
    offers_received: "Mottagna offerter", waiting: "Väntar", roster: "Lista",
    browse_gigs: "Bläddra bland uppdrag", new_request: "Ny förfrågan",
    welcome_back: "Välkommen tillbaka. Här är din bokningsaktivitet.",
    manage_roster: "Hantera din lista och boka uppdrag åt dem.",
    // Gigs
    gigs_intro: "Riktiga bokningsförfrågningar. Lås upp med en kredit för att se kontaktuppgifter och skicka en offert.",
    unlock: "Lås upp", send_offer: "Skicka offert", offer_sent: "Offert skickad",
    unlocked_badge: "Upplåst", fully_matched: "Fullt matchat", buy_to_unlock: "Köp krediter för att låsa upp",
    slots_left_one: "1 plats kvar av 5", slots_left_many: "{n} platser kvar av 5", slots_full: "Alla 5 platser tagna",
    // Empty states
    no_bookings: "Inga bekräftade bokningar",
    no_bookings_sub: "När en kund accepterar din offert visas den här med kontaktuppgifter.",
    download_contract: "Ladda ner kontrakt",
    no_offers: "Inga offerter skickade", no_offers_sub: "Lås upp ett uppdrag och skicka din första offert för att se den här.",
    // Offers
    accept: "Acceptera", decline: "Avböj", counter: "Motbud",
    accept_counter: "Acceptera motbud", client_countered_at: "Kunden lade motbud på",
    status_sent: "Skickad", status_countered: "Motbud", status_accepted: "Accepterad",
    status_declined: "Avböjd", status_expired: "Utgången", status_withdrawn: "Tillbakadragen", status_pending: "Väntar",
    status_pending_approval: "Väntar på godkännande", status_published: "Publicerad", status_matching: "Matchar", status_responded: "Besvarad",
    // Credit chip
    Buy: "Köp", top_up: "Toppa upp", buy_credits: "Köp krediter",
    // Talent dashboard
    profile_status_prefix: "Din profil är",
    profile_status_suffix: "Slutför din profil och vänta på admingodkännande för att synas publikt och få matchade uppdrag.",
    complete_profile: "Slutför din profil",
    complete_profile_sub_suffix: "% klart, nå 50% för att låsa upp allt och få 3 gratis krediter.",
    improve: "Förbättra", running_low: "Snart slut, toppa upp för att fortsätta låsa upp uppdrag.",
    credits_never_expire: "Krediter går aldrig ut", find_events: "Hitta event att boka",
    my_offers: "Mina offerter", track_pitches: "Följ dina bud",
    // Client dashboard
    recent_requests: "Senaste förfrågningar", view_offers: "Visa offerter",
    no_requests: "Ge dig inte ut på jakt", no_requests_sub: "Skicka vad du behöver, luta dig tillbaka och låt buden med pris rulla in.",
    post_request: "Skicka din första förfrågan", offers_label: "offerter",
    talent_live_header: "Kunder väntar", talent_live_sub: "Kolla tavlan med liveförfrågningar och skicka ett bud med pris för att säkra ditt nästa uppdrag.", talent_live_cta: "Visa liveförfrågningar",
    // Agency
    manage_roster_card: "Hantera lista",
    // Gigs
    filter: "Filtrera", clear_all: "Rensa alla", locked: "Låst",
    no_gigs_match: "Inga uppdrag matchar dina filter", try_clearing: "Prova att rensa några filter.",
    no_gigs: "Inga uppdrag tillgängliga", no_gigs_sub: "Nya matchade förfrågningar visas här. Kom tillbaka snart.",
    unlock_1_credit: "Lås upp (1 kredit)",
    filter_gigs: "Filtrera uppdrag", interest: "Intresse", all_interests: "Alla intressen",
    country: "Land", all_countries: "Alla länder", city: "Stad", all_cities: "Alla städer",
    reset: "Återställ", show: "Visa", gigs_lower: "uppdrag",
    // Client settings
    profile_tab: "Profil", notifications_tab: "Aviseringar", privacy_tab: "Integritet", account_tab: "Konto",
    profile_photo: "Profilbild", full_name: "Fullständigt namn", phone_number: "Telefonnummer", city_label: "Stad",
    account_type_label: "Kontotyp", private_label: "Privat", corporate_label: "Företag",
    company_name: "Företagsnamn", org_number: "Organisationsnummer", contact_person: "Kontaktperson",
    billing_email: "Fakturamejl", billing_email_hint: "Fakturor skickas hit", billing_information: "Faktureringsinformation",
    save: "Spara", save_prefs: "Spara inställningar",
    notif_new_offers: "Nya offerter på mina förfrågningar", notif_booking_conf: "Bokningsbekräftelser",
    notif_messages: "Meddelanden", notif_platform_news: "Plattformsnyheter",
    cookie_prefs: "Cookie-inställningar", essential_cookies: "Nödvändiga", essential_always_on: "Alltid på",
    analytics_cookies: "Analys", marketing_cookies: "Marknadsföring",
    your_data: "Dina uppgifter", request_data: "Begär dina uppgifter", request_data_desc: "Vi mejlar en kopia av dina uppgifter till dig.",
    request_deletion: "Begär radering av konto",
    email_label: "E-post", change_email: "Byt e-post", change_email_prompt: "Ange din nya e-postadress",
    change_password: "Byt lösenord",
    delete_account: "Radera konto", delete_account_desc: "Detta tar permanent bort ditt konto och dina uppgifter. Kan inte ångras.",
    delete_type_confirm: "Skriv DELETE för att bekräfta", confirm_delete: "Radera mitt konto", cancel: "Avbryt",
    your_full_name: "Ditt fullständiga namn", settings_title: "Kontoinställningar",
    // Titles / shared
    "Credit history": "Kredithistorik", "Search...": "Sök...",
    // Email verify banner
    "Please confirm your email address. Check your inbox for a link from Talentsio.": "Bekräfta din e-postadress. Kolla din inkorg efter en länk från Talentsio.",
    "Resend email": "Skicka mejlet igen", "Sent!": "Skickat!",
    // Language toggle
    "Language": "Språk",
    "New request": "Ny förfrågan", "Incoming offers": "Mottagna offerter",
    // Client bookings
    "No bookings yet": "Inga bokningar än",
    "Accepted offers become confirmed bookings and show up here.": "Accepterade offerter blir bekräftade bokningar och visas här.",
    "Talent": "Talang",
    // Client talents
    "You can see talents who applied to each of your gigs. Save a talent to keep access after the event window closes.": "Här ser du talanger som sökt till dina uppdrag. Spara en talang för att behålla åtkomsten efter att eventfönstret stängts.",
    "No applicants yet": "Inga sökande än",
    "Post a request and talents who apply will appear here, grouped by gig.": "Skicka en förfrågan så visas talanger som söker här, grupperade per uppdrag.",
    "applied": "sökande", "Leave a review": "Lämna ett omdöme",
    "Saved talents": "Sparade talanger", "Past events": "Tidigare event",
    "window closed": "fönstret stängt", "Save talents to keep access": "Spara talanger för att behålla åtkomst",
    "View profile": "Visa profil", "Message": "Meddelande", "Saved": "Sparad", "Save talent": "Spara talang",
    "Removed from saved": "Borttagen från sparade", "Talent saved": "Talang sparad", "Failed": "Misslyckades",
    "Could not start conversation": "Kunde inte starta konversation",
    "Hi! I'd love to discuss my event with you.": "Hej! Jag skulle gärna diskutera mitt event med dig.",
    // Offers hub
    "No offers yet": "Inga offerter än",
    "Once talents respond to your requests, their offers appear here to compare.": "När talanger svarar på dina förfrågningar visas deras offerter här så att du kan jämföra.",
    "Request": "Förfrågan", "Talent requirements": "Talangens krav",
    "No specific requirements listed.": "Inga specifika krav angivna.",
    "You countered at": "Du lade motbud på", "awaiting talent response": "väntar på talangens svar",
    "Accept": "Acceptera", "Decline": "Avböj", "Counter": "Motbud",
    "Send counter-offer": "Skicka motbud", "Your counter price (SEK)": "Ditt motbudspris (SEK)",
    "Message (optional)": "Meddelande (valfritt)",
    "Booking confirmed!": "Bokning bekräftad!", "Contact details are now unlocked.": "Kontaktuppgifterna är nu upplåsta.",
    "Got it": "Uppfattat", "Offer accepted, booking confirmed!": "Offert accepterad, bokning bekräftad!",
    "Done": "Klart", "Action failed": "Åtgärden misslyckades",
    // Agency roster
    "Invite talents to your roster. You can send offers on their behalf once they join.": "Bjud in talanger till din lista. Du kan skicka offerter åt dem när de gått med.",
    "Talent email": "Talangens e-post", "Name (optional)": "Namn (valfritt)", "Invite talent": "Bjud in talang",
    "No talents yet": "Inga talanger än", "Invite your first talent using the form above.": "Bjud in din första talang med formuläret ovan.",
    "Active": "Aktiv", "Pending": "Väntar",
    "Talent linked to your roster!": "Talangen är kopplad till din lista!", "Invitation sent!": "Inbjudan skickad!",
    "Could not invite": "Kunde inte bjuda in", "Removed from roster": "Borttagen från listan", "Could not remove": "Kunde inte ta bort",
    // Referral page
    ref_hero_title: "Bjud in talanger och tjäna {n} krediter per person",
    ref_hero_body: "Dela din länk eller skicka en personlig e-postinbjudan. När någon går med som talang och bekräftar sin e-post får du {n} krediter.",
    "Copy link": "Kopiera länk", "Copied": "Kopierad", "Your code:": "Din kod:",
    "People invited": "Inbjudna personer", "Credits earned": "Intjänade krediter",
    "Send an email invite": "Skicka en e-postinbjudan",
    "Personalise the message and preview it live before sending.": "Anpassa meddelandet och förhandsgranska det live innan du skickar.",
    "Your invitations": "Dina inbjudningar", "Invite link copied": "Inbjudningslänken kopierad",
    // Invite form
    "Email language": "E-postspråk", "Template": "Mall", "Recipient email": "Mottagarens e-post",
    "Recipient name": "Mottagarens namn", "(optional)": "(valfritt)", "Subject": "Ämne",
    "Send invitation": "Skicka inbjudan", "Sending…": "Skickar…", "Live preview": "Live-förhandsvisning",
    "Your unique invite link is always added automatically and can't be removed.": "Din unika inbjudningslänk läggs alltid till automatiskt och kan inte tas bort.",
    "Enter a valid email address": "Ange en giltig e-postadress",
    "Could not send invitation": "Kunde inte skicka inbjudan", "Invitation sent to": "Inbjudan skickad till",
    "Registered": "Registrerad", "Completed": "Slutförd", "Opted out": "Avanmäld",
    "Opened": "Öppnad", "Clicked": "Klickad", "Resend": "Skicka igen",
    // Availability
    "Set the days you usually work and block specific dates. Confirmed bookings are blocked automatically. Organisers see a warning if their event date clashes with yours.": "Ställ in vilka dagar du vanligtvis jobbar och blockera specifika datum. Bekräftade bokningar blockeras automatiskt. Arrangörer ser en varning om deras eventdatum krockar med ditt.",
    "Weekly availability": "Veckotillgänglighet",
    "Green = you generally take bookings on this day.": "Grönt = du tar vanligtvis bokningar denna dag.",
    "Block specific dates": "Blockera specifika datum",
    "Blocked by you": "Blockerad av dig", "Confirmed booking": "Bekräftad bokning", "Day off": "Ledig dag",
    "Save availability": "Spara tillgänglighet", "Availability saved": "Tillgänglighet sparad",
    "Could not save availability": "Kunde inte spara tillgänglighet",
    "This date has a confirmed booking and is auto-blocked": "Detta datum har en bekräftad bokning och är automatiskt blockerat",
    Mon: "Mån", Tue: "Tis", Wed: "Ons", Thu: "Tor", Fri: "Fre", Sat: "Lör", Sun: "Sön",
    January: "Januari", February: "Februari", March: "Mars", April: "April", May: "Maj", June: "Juni",
    July: "Juli", August: "Augusti", September: "September", October: "Oktober", November: "November", December: "December",
    // Messages
    "No messages yet": "Inga meddelanden än",
    "Message a talent from their profile, your gig's applicants, or a saved talent, to start a conversation.": "Skriv till en talang från deras profil, dina sökande eller en sparad talang för att starta en konversation.",
    "Select a conversation": "Välj en konversation", "New message": "Nytt meddelande",
    "Talentsio does not handle direct payments. Remember to agree on payment terms directly before the gig.": "Talentsio hanterar inte direkta betalningar. Kom ihåg att komma överens om betalningsvillkor direkt före uppdraget.",
    "You": "Du", "Write your first message to start the conversation.": "Skriv ditt första meddelande för att starta konversationen.",
    "Write a message...": "Skriv ett meddelande...",
    // Support
    Billing: "Fakturering", Account: "Konto", "Booking / Gig": "Bokning / Uppdrag", Technical: "Tekniskt", Other: "Övrigt",
    "How can we help?": "Hur kan vi hjälpa dig?",
    "Browse the FAQ for quick answers, send us a message, or open a ticket to track a longer conversation. We usually reply within a day.": "Bläddra i FAQ:n för snabba svar, skicka ett meddelande eller öppna ett ärende för en längre konversation. Vi svarar oftast inom en dag.",
    "Frequently asked questions": "Vanliga frågor",
    "Send us a message": "Skicka ett meddelande till oss",
    "Quick question? Send a message and we will reply to": "Snabb fråga? Skicka ett meddelande så svarar vi till",
    "Subject (optional)": "Ämne (valfritt)", "Send message": "Skicka meddelande",
    "Your tickets": "Dina ärenden", "Track longer conversations with our team.": "Följ längre konversationer med vårt team.",
    "New ticket": "Nytt ärende", "No tickets yet": "Inga ärenden än",
    "Open a ticket and our team will get back to you.": "Öppna ett ärende så återkommer vårt team till dig.",
    "Back to tickets": "Tillbaka till ärenden", "opened": "öppnat", "Write a reply...": "Skriv ett svar...",
    "Submit ticket": "Skicka ärende", "Cancel": "Avbryt",
    "Ticket created": "Ärende skapat", "Write a message first": "Skriv ett meddelande först",
    "Message sent, we will reply by email.": "Meddelandet skickat, vi svarar via e-post.",
    "Could not send message": "Kunde inte skicka meddelandet", "Could not send": "Kunde inte skicka",
    "Could not create ticket": "Kunde inte skapa ärende",
    "open": "öppet", "in progress": "pågår", "resolved": "löst", "closed": "stängt",
    "How do credits work?": "Hur fungerar krediter?",
    "Talents spend 1 credit to unlock a booking request and see the organiser's contact details. Posting requests is always free for organisers.": "Talanger använder 1 kredit för att låsa upp en bokningsförfrågan och se arrangörens kontaktuppgifter. Att skicka förfrågningar är alltid gratis för arrangörer.",
    "How quickly will I get offers?": "Hur snabbt får jag offerter?",
    "Most organisers receive their first offers within a few hours. Verified talents respond directly to your request.": "Verifierade talanger svarar med prissatta offerter direkt i din inbox.",
    "How do I get verified?": "Hur blir jag verifierad?",
    "Add your organisation number under Settings, Account. Our team reviews it and grants a Verified badge on your public profile.": "Lägg till ditt organisationsnummer under Inställningar, Konto. Vårt team granskar det och ger dig en verifierad-badge på din publika profil.",
    "Can I get a refund on credits?": "Kan jag få återbetalning på krediter?",
    "Credits are non-refundable once a request is unlocked. Reach out below if something went wrong and we will help.": "Krediter återbetalas inte när en förfrågan har låsts upp. Hör av dig nedan om något gick fel så hjälper vi dig.",
    "How do I change my payout or rate?": "Hur ändrar jag mitt arvode eller pris?",
    "Open Settings, Pricing and pick your typical rate range. It uses the same bands organisers see when booking.": "Öppna Inställningar, Priser och välj ditt typiska prisintervall. Det använder samma intervall som arrangörer ser vid bokning.",
    // Settings shells
    "Profile": "Profil", "Location": "Plats", "Bio & media": "Bio och media", "Links": "Länkar",
    "Privacy": "Integritet", "Pricing": "Priser", "Logistics & terms": "Logistik och villkor",
    "Bio": "Bio", "Logistics": "Logistik",
    "Talent settings": "Talanginställningar", "Agency settings": "Agenturinställningar",
    "Profile photo": "Profilbild", "Display name": "Visningsnamn", "Experience (years)": "Erfarenhet (år)",
    "Categories": "Kategorier", "Specialities": "Specialiteter", "Genres / styles": "Genrer / stilar",
    "Languages": "Språk", "Hourly rate (SEK)": "Timpris (SEK)",
    "Short bio": "Kort bio", "Full bio": "Fullständig bio", "Portfolio media": "Portfoliomedia",
    "Equipment provided": "Utrustning som medföljer", "Technical requirements": "Tekniska krav",
    "Private address": "Privat adress", "Contact email": "Kontaktmejl", "Phone": "Telefon",
    "Change password": "Byt lösenord", "Business verification": "Företagsverifiering",
    "Add your organisation number to earn a Verified badge on your public profile.": "Lägg till ditt organisationsnummer för att få en verifierad-badge på din publika profil.",
    "Your data": "Dina uppgifter",
    "Export a copy of your data or request account deletion.": "Exportera en kopia av dina uppgifter eller begär radering av kontot.",
    "Manage data & privacy": "Hantera uppgifter och integritet",
    "Notifications": "Aviseringar", "New gig matches": "Nya matchade uppdrag",
    "Offer & booking updates": "Offert- och bokningsuppdateringar", "Product news": "Produktnyheter",
    "Agency logo": "Agenturlogotyp", "Agency name": "Agenturnamn", "Media": "Media",
    "Showcase your roster and past events.": "Visa upp din lista och tidigare event.",
    "Add profile photo": "Lägg till profilbild", "Add display name": "Lägg till visningsnamn",
    "Pick a category": "Välj en kategori", "Pick a speciality": "Välj en specialitet",
    "Add genres": "Lägg till genrer", "Write a short bio": "Skriv en kort bio",
    "Write a full bio": "Skriv en fullständig bio", "Set your rate": "Ange ditt pris",
    "Add your city": "Lägg till din stad", "Add portfolio media": "Lägg till portfoliomedia",
    "Add a link": "Lägg till en länk", "Add agency logo": "Lägg till agenturlogotyp",
    "Add agency name": "Lägg till agenturnamn", "Add media": "Lägg till media",
    "Available across all of Sweden": "Tillgänglig i hela Sverige",
    // Talent settings — pricing
    "Typical rate (SEK)": "Typiskt pris (SEK)",
    "Pick every price band you actually work in. Talents who cover more bands appear in more matched gigs.":
      "Välj alla prisintervall du faktiskt jobbar inom. Talanger som täcker fler intervall syns i fler matchade uppdrag.",
    "Optional, shown as a guide for shorter bookings.": "Valfritt, visas som guide för kortare bokningar.",
    // Talent settings — profile hints
    "Pick every category you perform in.": "Välj alla kategorier du uppträder i.",
    "Choose the specialities that apply within each category.": "Välj specialiteter som gäller inom varje kategori.",
    "English is included for everyone. Add any others you speak.":
      "Engelska ingår för alla. Lägg till fler språk du talar.",
    // Talent settings — location
    "Service areas": "Serviceområden",
    "Type a city and press Enter": "Skriv en stad och tryck Enter",
    // Talent settings — bio placeholders
    "One line that sums up what you do, e.g. High-energy wedding band from Stockholm.":
      "En rad som sammanfattar vad du gör, t.ex. Energiskt bröllopsband från Stockholm.",
    "Tell organisers about your experience, style and what makes your act special.":
      "Berätta för arrangörer om din erfarenhet, stil och vad som gör din akt speciell.",
    "Street, postcode, city": "Gata, postnummer, stad",
    // Settings progress header
    "Profile live": "Profilen live",
    "reach 50% to go live": "nå 50% för att gå live",
    "Saving…": "Sparar…", "Saved": "Sparat",
    "Save changes": "Spara ändringar",
    "Improve": "Förbättra",
    // File upload
    "Upload photo": "Ladda upp bild",
    "Delete profile image": "Radera profilbild",
    "JPG, PNG, WEBP or iPhone HEIC. Max 15 MB.": "JPG, PNG, WEBP eller iPhone HEIC. Max 15 MB.",
    "Upload photo, video or audio": "Ladda upp bild, video eller ljud",
    // Custom links editor
    "No links yet. Add Instagram, TikTok, Spotify, a website and more.":
      "Inga länkar än. Lägg till Instagram, TikTok, Spotify, en webbplats och mer.",
    "Add link": "Lägg till länk",
    "Label (e.g. Instagram)": "Etikett (t.ex. Instagram)",
    // Account tab
    "All emails from Talentsio will be sent in this language.":
      "Alla mejl från Talentsio skickas på detta språk.",
    "You signed in with Google, so there is no password to change.":
      "Du loggade in med Google, så det finns inget lösenord att byta.",
    "Active sessions": "Aktiva sessioner",
    "Signed in somewhere you don't recognise? Sign out of every other device instantly.":
      "Inloggad någonstans du inte känner igen? Logga ut från alla andra enheter direkt.",
    "Sign out all other devices": "Logga ut alla andra enheter",
    "Current password": "Nuvarande lösenord",
    "New password": "Nytt lösenord",
    "Confirm new password": "Bekräfta nytt lösenord",
    "Enter your current password": "Ange ditt nuvarande lösenord",
    "Choose a new password (min. 8 characters)": "Välj ett nytt lösenord (minst 8 tecken)",
    "Repeat your new password": "Upprepa ditt nya lösenord",
    "Sign out of all other devices": "Logga ut från alla andra enheter",
    "Keeps you signed in here and logs out everywhere else.":
      "Håller dig inloggad här och loggar ut överallt annars.",
    "Update password": "Uppdatera lösenord",
    // Business verification
    "Org. number (e.g. 556677-8899)": "Org.nummer (t.ex. 556677-8899)",
    "Submit": "Skicka",
    // Offer modal
    "Booking request": "Bokningsförfrågan",
    "Performing talent": "Utförande talang",
    "Our agency (no specific roster talent)": "Vår agentur (ingen specifik talang från listan)",
    "Pick who from your roster performs this gig. Shown to the organiser on the offer.": "Välj vem från din lista som utför uppdraget. Visas för arrangören på offerten.",
    "Fee (SEK, ex. VAT)": "Arvode (SEK, ex. moms)",
    "VAT (main)": "Moms (huvudsats)",
    "e.g. 15000": "t.ex. 15000",
    "You are not VAT-registered, so VAT is not added. Add your VAT registration number in settings to invoice with VAT.": "Du är inte momsregistrerad, så moms läggs inte på. Lägg till ditt momsregistreringsnummer i inställningarna för att fakturera med moms.",
    "6% (cultural)": "6% (kultur)",
    "0% (No VAT)": "0% (Ingen moms)",
    "Add-ons (each with its own VAT)": "Tillägg (varje med egen moms)",
    "Add line": "Lägg till rad",
    "Optional: extra performance hour, travel, sound package etc.": "Valfritt: extra speltimme, resa, ljudpaket med mera.",
    "Item name": "Benämning",
    "Discount (SEK)": "Rabatt (SEK)",
    "Performance length (hours)": "Speltid (timmar)",
    "e.g. 3": "t.ex. 3",
    "hour": "timme", "hours": "timmar",
    "What is included": "Vad som ingår",
    "Sound system": "Ljudsystem", "Lighting": "Ljus", "Microphones": "Mikrofoner",
    "Travel included": "Resa ingår", "Setup and teardown": "Montering och nedmontering",
    "Payment terms": "Betalningsvillkor",
    "Betalning på plats": "Betalning på plats", "10 dagar netto": "10 dagar netto",
    "20 dagar netto": "20 dagar netto", "30 dagar netto": "30 dagar netto",
    "Load-in": "Inlastning", "Finish": "Sluttid",
    "Offer valid for (days)": "Offerten gäller i (dagar)", "days": "dagar",
    "Cancellation policy": "Avbokningsvillkor",
    "Flexible": "Flexibel", "Strict": "Strikt",
    "Full refund up to 7 days before": "Full återbetalning fram till 7 dagar före",
    "Full refund up to 14 days before": "Full återbetalning fram till 14 dagar före",
    "Full refund up to 30 days before": "Full återbetalning fram till 30 dagar före",
    "Personal pitch (optional)": "Personlig pitch (valfritt)",
    "Why you're the right talent for this event.": "Varför du är rätt talang för detta event.",
    "Live summary": "Sammanfattning i realtid",
    "Net": "Netto", "VAT (blended)": "Moms (blandad)",
    "Total incl. VAT": "Totalt inkl. moms",
    // Tax and legal settings card + offers hub
    "Tax and legal": "Skatt och juridik",
    "These details appear on your offers, contracts and invoices.": "Dessa uppgifter visas på dina offerter, avtal och fakturor.",
    "Legal name": "Juridiskt namn",
    "E.g. Anna Andersson AB or your full name": "T.ex. Anna Andersson AB eller ditt fullständiga namn",
    "Approved for F-tax": "Godkänd för F-skatt",
    "VAT registration number": "Momsregistreringsnummer",
    "Optional. Fill in if you are VAT-registered.": "Valfritt. Fyll i om du är momsregistrerad.",
    "Address": "Adress",
    "Never shown publicly, only shared after a confirmed booking.": "Visas aldrig offentligt, delas först efter en bekräftad bokning.",
    "Contract ready": "Kontrakt klart",
    "Performed by": "Utförs av",
    // Gigs page (pass 3)
    "to": "till", "Up to": "Upp till", "Contact": "Kontakt", "Closed": "Stängt",
    "guests": "gäster", "event has passed": "eventet har passerat",
    "Past events are shown as a preview only and can no longer be unlocked.": "Tidigare event visas endast som förhandsvisning och kan inte längre låsas upp.",
    "You're out of credits. Top up to unlock new gigs.": "Du har slut på krediter. Fyll på för att låsa upp nya uppdrag.",
    "Only {c} left. Top up so you never miss a gig.": "Bara {c} kvar. Fyll på så att du aldrig missar ett uppdrag.",
    "Your gig alerts": "Dina uppdragsbevakningar",
    "Save as gig alert": "Spara som uppdragsbevakning",
    "All gigs": "Alla uppdrag",
    "Unlock this gig?": "Lås upp detta uppdrag?",
    "This unlock is free.": "Denna upplåsning är gratis.",
    "welcome credit left.": "välkomstkredit kvar.", "welcome credits left.": "välkomstkrediter kvar.",
    "This uses": "Detta använder", "You'll have": "Du har", "left.": "kvar.",
    "Unlocking reveals the organiser's contact details so you can send an offer.": "Upplåsning visar arrangörens kontaktuppgifter så att du kan skicka en offert.",
    "Unlocking...": "Låser upp...", "Unlock now": "Lås upp nu",
    "Gig alert saved. We'll notify you on matches.": "Uppdragsbevakning sparad. Vi meddelar dig vid matchningar.",
    "Not enough credits. Top up to unlock this gig.": "Inte tillräckligt med krediter. Fyll på för att låsa upp detta uppdrag.",
    "This gig can no longer be unlocked.": "Detta uppdrag kan inte längre låsas upp.",
    "Could not unlock the gig.": "Kunde inte låsa upp uppdraget.",
    "Offer sent!": "Offert skickad!",
    "Could not send offer": "Kunde inte skicka offerten",
    "Unlocked with a free welcome credit.": "Upplåst med en gratis välkomstkredit.",
    "free left.": "gratis kvar.",
    "Gig unlocked!": "Uppdrag upplåst!",
    "unlocks!": "upplåsningar!",
    "First unlock! You're on your way.": "Första upplåsningen! Du är på väg.",
    "5 gigs unlocked, keep the momentum going!": "5 uppdrag upplåsta, håll tempot uppe!",
    "10 unlocks! You're a Talentsio regular.": "10 upplåsningar! Du är en Talentsio-stammis.",
    "25 unlocks, that's real hustle.": "25 upplåsningar, det är riktigt driv.",
    "50 unlocks! Serious booking machine.": "50 upplåsningar! En riktig bokningsmaskin.",
    "100 unlocks! Legendary.": "100 upplåsningar! Legendariskt.",
    // Gig-detalj, låst och upplåst
    "Could not load the gig.": "Kunde inte hämta uppdraget.",
    "Gig not found": "Uppdraget finns inte",
    "The request has been removed, or it was never published.": "Förfrågan är borttagen, eller publicerades aldrig.",
    "This event has already taken place, so it can no longer be unlocked.": "Eventet har redan ägt rum och kan inte längre låsas upp.",
    "Date": "Datum", "City": "Stad", "Event type": "Typ av event", "Category": "Kategori",
    "Email": "E-post", "Phone": "Telefon",
    "Why this gig matches you": "Varför uppdraget matchar dig",
    "This request matches your exact speciality, so it is ranked first in your feed.": "Förfrågan matchar din exakta specialitet, därför ligger den först i ditt flöde.",
    "This request is in a category you work in, so it is ranked above the rest of the feed.": "Förfrågan ligger i en kategori du jobbar i, därför ligger den före resten av flödet.",
    "Customer details": "Kundens uppgifter",
    "Locked details": "Låsta uppgifter",
    "Unlock to see": "Lås upp för att se",
    "Message to the talent": "Meddelande till artisten",
    "You have this gig unlocked": "Du har låst upp uppdraget",
    "Your offer is with the customer. You can revise it from My offers.": "Din offert ligger hos kunden. Du kan ändra den under Mina offerter.",
    "Send a personal offer with a fixed total price. The customer sees exactly what they pay.": "Skicka en personlig offert med fast totalpris. Kunden ser exakt vad hen betalar.",
    "Five talents have already taken the spots on this request. Nothing has been charged.": "Fem talanger har redan tagit platserna på förfrågan. Ingenting har dragits.",
    "Free with your welcome bonus": "Fri med din välkomstbonus",
    "Unlock to send an offer": "Lås upp för att skicka offert",
    "Contact details, the exact address and the customer's message become visible. Then you can send a binding offer.": "Kontaktuppgifter, exakt adress och kundens meddelande blir synliga. Sedan kan du lämna en bindande offert.",
    "You have": "Du har", "free unlocks left.": "fria upplåsningar kvar.", "Balance": "Saldo",
    "Use a free unlock": "Använd en fri upplåsning",
    "Verified customer, email confirmed": "Verifierad kund, e-post bekräftad",
    "Refund if the request turns out to be invalid": "Återbetalning om förfrågan visar sig vara ogiltig",
    "No obligation to send an offer": "Ingen skyldighet att skicka offert",
    "offer so far": "offert hittills", "offers so far": "offerter hittills",
    "Answering quickly matters more than answering cheaply. Most customers reply to the first offer they read.": "Att svara snabbt betyder mer än att svara billigt. De flesta kunder svarar på den första offerten de läser.",
    // Skatt och rapportering, DAC7
    "Tax and reporting": "Skatt och rapportering",
    "Could not load your tax status.": "Kunde inte hämta din skattestatus.",
    "Tax details saved.": "Skatteuppgifterna är sparade.",
    "Could not save your tax details.": "Kunde inte spara skatteuppgifterna.",
    "Everything is in order": "Allt är i ordning",
    "Your details are complete. We report to Skatteverket for you, you do not need to do anything.": "Dina uppgifter är kompletta. Vi rapporterar till Skatteverket åt dig, du behöver inte göra något.",
    "One detail is missing": "En uppgift saknas",
    "details are missing": "uppgifter saknas",
    "Your payouts are on hold until the details are complete. Fill them in below and they resume immediately.": "Dina utbetalningar är pausade tills uppgifterna är kompletta. Fyll i dem nedan så släpps de direkt.",
    "Without them we cannot file the DAC7 report, and payouts can be paused in January.": "Utan dem kan vi inte lämna DAC7-rapporten, och utbetalningar kan pausas i januari.",
    "DAC7 reporting": "DAC7-rapportering",
    "Complete": "Kompletta", "Incomplete": "Ofullständiga", "Not required yet": "Krävs inte än",
    "DAC7 is an EU requirement: digital platforms report what their sellers earn to the tax authority once a year. Talentsio does it for you. You do not file anything yourself.": "DAC7 är ett EU-krav: digitala plattformar rapporterar årligen vad deras säljare tjänat till Skatteverket. Talentsio gör det åt dig. Du lämnar ingenting själv.",
    "Reporting year": "Rapporteringsår",
    "Filed no later than": "Rapporteras senast",
    "Amount so far this year": "Belopp hittills i år",
    "Bookings so far this year": "Bokningar hittills i år",
    "Reported to": "Rapporteras till",
    "Reported per quarter, which is how the report is structured": "Redovisas per kvartal, vilket är så rapporten är uppbyggd",
    "booking": "bokning", "bookings": "bokningar",
    "Download your own figures": "Ladda ner ditt underlag",
    "Your details": "Dina uppgifter",
    "These go into the report and nowhere else. They are never shown on your public profile.": "De går in i rapporten och ingen annanstans. De visas aldrig på din publika profil.",
    "Legal name": "Juridiskt namn",
    "As registered with Skatteverket or Bolagsverket": "Som det står hos Skatteverket eller Bolagsverket",
    "Personal or organisation number": "Personnummer eller organisationsnummer",
    "Tax residency": "Skatterättslig hemvist",
    "Date of birth": "Födelsedatum",
    "Street address": "Gatuadress", "Postal code": "Postnummer",
    "Saving...": "Sparar...", "Save": "Spara",
    "Stored encrypted, never shown publicly": "Lagras krypterat, visas aldrig publikt",
    "F-skatt": "F-skatt", "Approved": "Godkänd", "Not verified": "Inte verifierad",
    "Organisation number": "Organisationsnummer", "Verify F-skatt": "Verifiera F-skatt",
    "VAT": "Moms", "VAT registered": "Momsregistrerad", "VAT number": "Momsnummer",
    "Fee": "Gage", "percent VAT": "procent moms",
    "VAT on artist fees is 0 percent under chapter 10, section 31 of the Swedish VAT act. Equipment and travel are invoiced at their own rates. This does not affect what is reported under DAC7, which is the gross amount you receive.": "Moms på gage är 0 procent enligt momslagen 10 kap. 31 §. Utrustning och resa faktureras med respektive momssats. Det påverkar inte vad som rapporteras enligt DAC7, som är bruttobeloppet du får.",
    "days left until we have to file. Payouts are paused for talents whose details are still missing when the deadline passes.": "dagar kvar tills vi måste rapportera. Utbetalningar pausas för talanger vars uppgifter fortfarande saknas när tiden går ut.",
    // Final sweep (pass 4)
    "New password must be at least 8 characters": "Det nya lösenordet måste vara minst 8 tecken",
    "New passwords do not match": "De nya lösenorden matchar inte",
    "Password updated. Other devices signed out.": "Lösenordet uppdaterat. Andra enheter utloggade.",
    "Password updated": "Lösenordet uppdaterat",
    "Could not update password": "Kunde inte uppdatera lösenordet",
    "Signed out of all other devices": "Utloggad från alla andra enheter",
    "Could not sign out other devices": "Kunde inte logga ut andra enheter",
    "Could not open contract": "Kunde inte öppna kontraktet",
    "Client": "Kund",
    "Agency Dashboard": "Agenturöversikt",
    "Gig feed": "Uppdragsflöde",
    "My bookings": "Mina bokningar",
    "Browse talents": "Bläddra bland talanger",
    "e.g. 2500": "t.ex. 2500", "e.g. 8": "t.ex. 8",
    "e.g. Full PA system, DJ decks and lighting rig included.": "t.ex. Komplett PA-system, DJ-utrustning och ljusrigg ingår.",
    "e.g. Two 230V sockets, a 3x2m stage area and load-in access.": "t.ex. Två 230V-uttag, en scenyta på 3x2 m och tillgång för inlastning.",
    "e.g. Full refund up to 14 days before the event, 50% within 7 days.": "t.ex. Full återbetalning fram till 14 dagar före eventet, 50% inom 7 dagar.",
    "e.g. Free within Stockholm, travel fee applies beyond 50km.": "t.ex. Gratis inom Stockholm, reseavgift tillkommer bortom 50 km.",
    "Describe your technical requirements, equipment needs, stage setup, hospitality requests etc. This will be visible to clients who book you.": "Beskriv dina tekniska krav, utrustningsbehov, scenuppsättning, hospitality med mera. Detta visas för kunder som bokar dig.",
    // Offer detail modal (talent side)
    "Offer details": "Offertdetaljer",
    "View details": "Visa detaljer",
    "Withdraw": "Dra tillbaka",
    "Withdraw offer": "Dra tillbaka offert",
    "Yes, withdraw": "Ja, dra tillbaka",
    "Confirm?": "Bekräfta?",
    "Keep": "Behåll",
    "Financial summary": "Ekonomisk sammanfattning",
    "Subtotal": "Delsumma",
    "Discount": "Rabatt",
    "Total incl. VAT": "Total inkl. moms",
    "Currency": "Valuta",
    "Add-ons": "Tillägg",
    "Terms": "Villkor",
    "Speltid": "Speltid",
    "Payment terms": "Betalningsvillkor",
    "Cancellation": "Avbokning",
    "Valid until": "Giltig till",
    "Schedule": "Schema",
    "Load-in": "Inlastning",
    "Finish": "Avslut",
    "optional": "valfritt",
    "What's included": "Vad ingår",
    "Personal pitch": "Personlig pitch",
    "Requirements": "Krav",
    "Client counter": "Kundens motbud",
    "Counter price": "Motbudspris",
    "Message": "Meddelande",
    "Withdrawn": "Tillbakadragen",
    "Locked — booked": "Låst — bokad",
    "View contract": "Visa kontrakt",
    "Close": "Stäng",
    "Reference": "Referens",
    "Offer withdrawn": "Offert tillbakadragen",
    "Could not withdraw offer": "Kunde inte dra tillbaka offerten",
    "Sent": "Skickad",
    "just now": "just nu",
    "Expired": "Utgången",
    "Valid:": "Giltig:",
    // Withdraw reason picker
    "Why are you withdrawing?": "Varför drar du tillbaka?",
    "Reason (optional)": "Anledning (valfritt)",
    "Not available": "Inte tillgänglig",
    "Wrong scope": "Fel omfattning",
    "Price too low": "För lågt pris",
    "Other reason": "Annan anledning",
    "Add a short note (optional)": "Lägg till en kort notis (valfritt)",
    "Skip and withdraw": "Hoppa över och dra tillbaka",
    // Client OffersHub — dead-offer notes
    "Talent withdrew this offer": "Talangen drog tillbaka denna offert",
    "This offer expired without a response": "Denna offert löpte ut utan svar",
    "This offer is no longer active — you can view the other offers below.": "Denna offert är inte längre aktiv — du kan se andra offerter nedan.",
    "Reason:": "Anledning:",
    "reason_not_available": "Talangen är inte tillgänglig",
    "reason_wrong_scope": "Fel omfattning",
    "reason_price_too_low": "För lågt pris",
    "reason_other": "Annan anledning",
    // Gig-feed filter additions
    "Showing": "Visar",
    "of": "av",
    "Event date": "Eventdatum",
    "Budget (SEK)": "Budget (SEK)",
    "From": "Från",
    "To": "Till",
    "Min": "Min",
    "Max": "Max",
    // Offer-flow rename: "Load-in" is now surfaced as "Arrival Time".
    "Arrival Time": "Ankomsttid",
    // Mandatory-pitch validation copy
    "Please write at least 20 characters — clients read this first.": "Skriv minst 20 tecken — det är det första kunden läser.",
    "A short pitch dramatically increases your accept rate.": "En kort pitch ökar chansen att bli vald markant.",
    "Why you're the right talent for this event. (min 20 characters)": "Varför du är rätt talang för eventet. (min 20 tecken)",
    // A3 add-on picker (offer builder)
    "Import add-on": "Hämta in tillägg",
    "Pick from your saved add-ons. A copy is added to this offer.": "Välj bland dina sparade tillägg. En kopia läggs in i den här offerten.",
    "From catalog": "Från registret",
    "Quantity": "Antal",
    "piece": "styck",
    "evening": "kväll",
    "km": "mil",
    "occasion": "tillfälle",
    // Brief 3 Fas 1 (contract approval)
    "Approve": "Godkänn",
    "Approving": "Godkänner",
    "By approving you commit to the booking under the terms above.": "Genom att godkänna binder du dig till bokningen enligt villkoren ovan.",
    "Approved. Next step is the payment.": "Godkänt. Nästa steg är betalningen.",
    "The client has approved the contract.": "Kunden har godkänt avtalet.",
    "Awaiting customer approval": "Väntar på kundens godkännande",
    "Could not approve": "Det gick inte att godkänna",
    // Layered acceptance (jurist-verifierade upplysningar)
    "Mentgroup acts in the artist's name. The artist is the seller and the counterparty for the performance.": "Mentgroup agerar i artistens namn. Artisten är säljare och motpart för framförandet.",
    "Payment is handled by Stripe. Funds are held on the artist's Stripe account until payout.": "Betalningen hanteras av Stripe. Medlen hålls på artistens Stripe-konto fram till utbetalning.",
    "Cancellation terms": "Avbokningsvillkor",
    "The right of withdrawal under the Distance Selling Act does not apply to date-bound bookings.": "Ångerrätt enligt distansavtalslagen gäller inte datumbestämda bokningar.",
    "Booking total": "Bokningens totalbelopp",
    "Service fee 10%": "Serviceavgift 10 %",
    "VAT 25% on the fee": "Moms 25 % på avgiften",
    "I have read and accept Talentsio's general terms.": "Jag har läst och godkänner Talentsios allmänna villkor.",
    "I accept the terms of this offer.": "Jag godkänner villkoren i den här offerten.",
    "I accept the artist's cancellation policy as shown above.": "Jag godkänner artistens avbokningsvillkor som visas ovan.",
    "I understand that the right of withdrawal does not apply to date-bound bookings.": "Jag är införstådd med att ångerrätt inte gäller för datumbestämda bokningar.",
    "Please tick all consent boxes before approving.": "Kryssa i alla samtyckesrutor innan du godkänner.",
    // Brief 3 Fas 2 Step 2 (Payment settings + KYC page)
    "Payment settings": "Betalningsinställningar",
    "Get paid on Talentsio": "Så här får du betalt via Talentsio",
    "Not started": "Inte påbörjad",
    "Action needed": "Åtgärd krävs",
    "Under review": "Granskas av Stripe",
    "Verified": "Verifierad",
    "Connect your Stripe account so bookings on Talentsio can be paid directly to your bank. Onboarding is hosted by Stripe and takes about 3 minutes.": "Anslut ditt Stripe-konto så att bokningar på Talentsio betalas direkt till din bank. Onboardingen sker hos Stripe och tar cirka 3 minuter.",
    "How on-platform payment works": "Så fungerar betalning via plattformen",
    "Funds released 14 days after the event": "Utbetalning inom 14 dagar efter genomfört event",
    "The customer's payment is captured when the contract is approved, then held on your Stripe account. Stripe releases the payout the day after the event, once the booking has taken place without a dispute.": "Kundens betalning dras när avtalet godkänns och hålls sedan på ditt Stripe-konto. Stripe släpper utbetalningen dagen efter eventet när bokningen har genomförts utan tvist.",
    "The customer pays the 10% service fee": "Kunden betalar serviceavgiften på 10 %",
    "Talentsio's 10% (plus 25% VAT on the fee) is added on top of the booking total, so what you agreed with the customer is what you receive. You never lose part of your own fee.": "Talentsios 10 % (plus 25 % moms på avgiften) läggs ovanpå bokningens totalbelopp, så det du kom överens om med kunden är det du får. Du förlorar aldrig en del av ditt eget arvode.",
    "You are the seller and merchant of record": "Du är säljare och motpart",
    "Talentsio only facilitates the payment. Your VAT registration and F-skatt approval decide which invoice rules apply.": "Talentsio förmedlar bara betalningen. Din momsregistrering och F-skatt-godkännande styr vilka fakturaregler som gäller.",
    "F-skatt is required to receive on-platform payments": "F-skatt krävs för att ta emot betalningar via plattformen",
    "Stripe is set up correctly, but F-skatt approval is needed before we can enable payouts through the platform. Apply for F-skatt at Skatteverket and then update your invoice settings.": "Stripe är korrekt inställt, men F-skatt-godkännande krävs innan vi kan aktivera utbetalningar via plattformen. Ansök om F-skatt hos Skatteverket och uppdatera sedan dina fakturainställningar.",
    "Open invoice settings": "Öppna fakturainställningar",
    "Stripe Connect status": "Stripe Connect-status",
    "Refresh": "Uppdatera",
    "Account": "Konto",
    "No Stripe account linked yet.": "Inget Stripe-konto anslutet ännu.",
    "Done": "Klart",
    "Pending": "Väntar",
    "Details submitted": "Uppgifter inskickade",
    "You've completed Stripe's business and identity form.": "Du har fyllt i Stripes bolags- och identitetsformulär.",
    "Charges enabled": "Betalningar aktiverade",
    "Stripe will let us charge customers with you as the merchant of record.": "Stripe låter oss ta betalt av kunder med dig som säljare och motpart.",
    "Payouts enabled": "Utbetalningar aktiverade",
    "Money can be paid out to your bank account after the hold period.": "Pengar kan betalas ut till ditt bankkonto efter håll-perioden.",
    "F-skatt approved": "F-skatt godkänd",
    "Required by Skatteverket to invoice on-platform bookings through Talentsio.": "Krävs av Skatteverket för att fakturera bokningar via Talentsio.",
    "Redirecting to Stripe.": "Skickar dig vidare till Stripe.",
    "Update payout details": "Uppdatera utbetalningsuppgifter",
    "Continue onboarding": "Fortsätt onboardingen",
    "Connect with Stripe": "Anslut med Stripe",
    "On-platform payouts active": "Utbetalningar via plattformen aktiva",
    "Stripe is verifying your details. We'll update this page automatically.": "Stripe granskar dina uppgifter. Sidan uppdateras automatiskt.",
    "Stripe hosts and secures the onboarding form. Talentsio never sees your bank credentials or ID documents.": "Stripe står värd för och säkrar onboardingformuläret. Talentsio ser aldrig dina bankuppgifter eller ID-dokument.",
    "Support center": "Supportcenter",
    "Invoice settings": "Fakturainställningar",
    "Welcome back - refreshing your onboarding status.": "Välkommen tillbaka - vi hämtar din onboardingstatus.",
    "Onboarding link expired. Tap Continue to resume.": "Onboardinglänken har gått ut. Tryck Fortsätt för att fortsätta.",
    "Could not load Stripe status.": "Det gick inte att hämta Stripe-statusen.",
    "Could not start Stripe onboarding.": "Det gick inte att starta Stripe-onboardingen.",
    "Stripe Connect is not enabled on the platform account yet. Please contact support.": "Stripe Connect är inte aktiverat på plattformskontot ännu. Kontakta supporten.",
    // Brief 3 Fas 2 minimal checkout UI
    "Pay now": "Betala nu",
    "Checkout": "Kassa",
    "Back to offers": "Tillbaka till offerter",
    "Confirm your booking": "Bekräfta din bokning",
    "Contract": "Avtal",
    "Total to pay": "Att betala totalt",
    "Pay": "Betala",
    "Processing payment.": "Behandlar betalningen.",
    "Payment received. Funds are held on the artist's Stripe account until the event.": "Betalning mottagen. Pengarna hålls på artistens Stripe-konto fram till eventet.",
    "Payment failed. Please try again.": "Betalningen misslyckades. Försök igen.",
    "Could not start checkout.": "Det gick inte att starta kassan.",
    "Test mode: use card 4242 4242 4242 4242, any future date, any CVC.": "Testläge: använd kortet 4242 4242 4242 4242, valfritt framtida datum, valfri CVC.",

    // Error boundary
    "Something went wrong": "Något gick fel",
    "A part of this page failed to load. Try reloading, or go back to your dashboard.": "En del av sidan gick inte att ladda. Prova att ladda om, eller gå tillbaka till din dashboard.",
    "Technical details": "Tekniska detaljer",
    "Reload": "Ladda om",
    "Go to dashboard": "Till dashboarden",

    // Invoices (Brief 3 Fas 4)
    "Invoices": "Fakturor",
    "Create, send and follow up on invoices for your bookings.": "Skapa, skicka och följ upp fakturor för dina bokningar.",
    "New invoice": "Ny faktura",
    "Outstanding": "Utestående",
    "Paid this period": "Betalt i perioden",
    "Overdue": "Förfallet",
    "Drafts": "Utkast",
    "Search by number or customer": "Sök på nummer eller kund",
    "No invoices yet": "Inga fakturor än",
    "Create an invoice from a booking, or add a manual one.": "Skapa en faktura från en bokning eller lägg till en manuellt.",
    "Number": "Nummer",
    "Customer": "Kund",
    "Issued": "Utfärdad",
    "Due": "Förfaller",
    "Amount": "Belopp",
    "Status": "Status",
    "Draft": "Utkast",
    "Sent": "Skickad",
    "Paid": "Betald",
    "Cancelled": "Avbruten",
    "Send invoice": "Skicka faktura",
    "Mark as paid": "Markera som betald",
    "Marked as paid": "Markerad som betald",
    "Credit note": "Kreditnota",
    "Credit note created": "Kreditnota skapad",
    "Download PDF": "Ladda ner PDF",
    "Could not load invoices.": "Kunde inte ladda fakturor.",
    "Could not send invoice.": "Kunde inte skicka fakturan.",
    "Could not mark as paid.": "Kunde inte markera som betald.",
    "Could not download PDF.": "Kunde inte ladda ner PDF.",
    "Could not create invoice.": "Kunde inte skapa fakturan.",
    "Could not create credit note.": "Kunde inte skapa kreditnotan.",
    "Draft invoice created": "Utkast skapat",
    "Credit note for": "Kreditnota för",
    // NewInvoiceModal
    "Choose an existing customer": "Välj en befintlig kund",
    "New customer": "Ny kund",
    "Cancel": "Avbryt",
    "Save as draft": "Spara som utkast",
    "Lines": "Rader",
    "Add line": "Lägg till rad",
    "Description": "Beskrivning",
    "Qty": "Antal",
    "Unit price": "Á-pris",
    "Unit": "Enhet",
    "Issue date": "Fakturadatum",
    "Payment terms (days)": "Betalningsvillkor (dagar)",
    "Language": "Språk",
    "Reference": "Referens",
    "Optional note visible on the invoice.": "Valfri text som syns på fakturan.",
    "Subtotal ex VAT": "Summa ex moms",
    "VAT": "Moms",
    "Amount due": "Att betala",
    "Name": "Namn",
    "Email": "E-post",
    "Org. no.": "Org.nr",
    "VAT no.": "Momsreg.nr",
    "Address": "Adress",
    "Postcode": "Postnr",
    "City": "Ort",
    // CreditNoteModal
    "Crediting": "Krediterar",
    "Percent to credit": "Procent att kreditera",
    "Credit amount": "Kreditbelopp",
    "Reason": "Skäl",
    "Why is this invoice being credited?": "Varför krediteras fakturan?",
    "A credit note is immutable and gets its own INV number. It cannot be edited or deleted.": "En kreditnota är oföränderlig och får ett eget INV-nummer. Den kan inte redigeras eller raderas.",
    "Issue credit note": "Utfärda kreditnota",

    // Booking → Invoice CTA + Customers
    "Generate invoice": "Generera faktura",
    "Customers": "Kunder",
    "Everyone you have ever invoiced, from Talentsio bookings or added by you.": "Alla du någonsin fakturerat, från Talentsio-bokningar eller egen tillagda.",
    "New customer": "Ny kund",
    "Edit customer": "Redigera kund",
    "Customer added": "Kund tillagd",
    "Customer saved": "Kund sparad",
    "Customer removed": "Kund borttagen",
    "Could not load customers.": "Kunde inte ladda kunder.",
    "Could not save customer.": "Kunde inte spara kunden.",
    "Could not remove customer.": "Kunde inte ta bort kunden.",
    "Delete this customer? Sent invoices remain intact.": "Ta bort kunden? Skickade fakturor bevaras.",
    "Source": "Källa",
    "Type": "Typ",
    "Actions": "Åtgärder",
    "Company": "Företag",
    "Individual": "Privatperson",
    "Talentsio": "Talentsio",
    "Own": "Egen",
    "Invoice again": "Fakturera igen",
    "Edit": "Redigera",
    "Save": "Spara",
    "Country": "Land",
    "No customers yet": "Inga kunder än",
    "Customers appear here after your first sent invoice, or add one manually now.": "Kunder syns här efter din första skickade faktura, eller lägg till en manuellt nu.",
    "Search by name, email or org. no.": "Sök på namn, e-post eller org.nr",

    // Cancel modal (Fas 3 UI)
    "Cancel booking": "Avboka bokning",
    "Cancellation policy": "Avbokningsvillkor",
    "Days before event": "Dagar till evenemang",
    "Suggested refund": "Föreslagen återbetalning",
    "Reason for cancellation (visible to the artist)": "Skäl till avbokning (syns för artisten)",
    "Request cancellation": "Begär avbokning",
    "Cancellation request sent to the artist.": "Avbokningsförfrågan skickad till artisten.",
    "Could not submit request.": "Kunde inte skicka förfrågan.",
    "Choose how to respond:": "Välj hur du svarar:",
    "Accept suggested": "Acceptera föreslag",
    "Adjust refund": "Justera återbetalning",
    "Refund accepted and processed.": "Återbetalning accepterad och genomförd.",
    "Adjusted refund processed.": "Justerad återbetalning genomförd.",
    "Could not respond.": "Kunde inte svara.",
    "Cancel from my side (100% refund)": "Avboka från min sida (100 % återbetalning)",
    "Cancel this booking as the artist? Customer receives a 100% refund.": "Avboka som artist? Kunden får 100 % återbetalning.",
    "Booking cancelled and full refund issued.": "Bokning avbokad och full återbetalning skickad.",
    "Could not cancel.": "Kunde inte avboka.",
    "Loading quote...": "Laddar förslag...",
    "Could not load quote.": "Kunde inte ladda förslag.",

    // Feature-flag gated copy
    "On-platform payouts coming soon": "Utbetalningar via plattformen kommer snart",
    "Talentsio is finalising the on-platform payout rail with our accountant. Connecting Stripe will re-open shortly. Until then you invoice your customers directly.": "Talentsio slutför utbetalningsrälsen med vår revisor. Stripe-anslutningen öppnar strax igen. Fram tills dess fakturerar du kunderna direkt.",
    "On-platform payments coming soon": "Betalningar via plattformen kommer snart",
    "Card payments through Talentsio are being finalised with our accountant. Until then, arrange payment directly with the artist.": "Kortbetalningar via Talentsio färdigställs med revisorn. Kom överens om betalning direkt med artisten fram tills dess.",
    "Coming soon": "Kommer snart",
    "Sending invoices is coming soon while we finalise self-invoicing sign-off.": "Fakturasändning öppnar snart när självfaktureringen är godkänd.",
    "Credit notes reopen when sending is enabled.": "Kreditnotor öppnar igen när fakturasändning är aktiverad.",
    "Send (coming soon)": "Skicka (kommer snart)",

    // Admin platform invoices
    "Platform invoices": "Plattformsfakturor",
    "Cross-platform view of every invoice with VAT breakdown and gap-free number series audit.": "Plattformsövergripande vy av alla fakturor med momsuppdelning och kontroll av obruten nummerserie.",
    "Month": "Månad",
    "All": "Alla",
    "Search by number, buyer or seller": "Sök på nummer, köpare eller säljare",
    "Invoices in view": "Fakturor i vyn",
    "Total incl. VAT": "Totalt inkl moms",
    "VAT collected": "Inkasserad moms",
    "Sources": "Källor",
    "VAT breakdown per rate": "Momsuppdelning per sats",
    "No VAT": "Ingen moms",
    "on": "på",
    "lines": "rader",
    "Number series audit": "Nummerserie-kontroll",
    "All sellers gap-free": "Alla säljare utan luckor",
    "Gaps detected": "Luckor hittade",
    "sent": "skickade",
    "OK": "OK",
    "gap(s)": "lucka/or",
    "No invoices in this filter": "Inga fakturor för detta filter",
    "Seller": "Säljare",
    "Buyer": "Köpare",
    "Total": "Totalt",
    // Self-billing consent + preview
    "Review & send": "Granska & skicka",
    "Review & accept": "Granska & godkänn",
    "Accept the self-billing mandate to enable invoicing": "Godkänn självfaktureringsmandatet för att aktivera fakturering",
    "Talentsio issues invoices in your name. You review and approve every invoice before it is sent.":
      "Talentsio ställer ut fakturor i ditt namn. Du granskar och godkänner varje faktura innan den skickas.",
    "This is exactly what your customer will receive. Approve to send it.":
      "Detta är exakt vad kunden får. Godkänn för att skicka.",
    "Loading preview…": "Laddar förhandsgranskning…",
    "Could not load preview.": "Kunde inte ladda förhandsgranskning.",
    "Please accept the self-billing mandate first.": "Godkänn självfaktureringsmandatet först.",
    "Approve & send": "Godkänn & skicka",
    "Approved & sent by talent": "Godkänd & skickad av talangen",
    "Invoice sent.": "Fakturan skickades.",
    // Talent dashboard (SV translations for the EN keys added above).
    Home: "Hem", Book: "Boka",
    "Good night": "God natt", "Good morning": "God morgon", "Good day": "God förmiddag",
    "Good afternoon": "God eftermiddag", "Good evening": "God kväll",
    "Could not load the overview. Please reload the page.": "Kunde inte läsa in översikten. Ladda om sidan.",
    "Today's focus": "Dagens fokus",
    "A payout did not arrive": "En utbetalning kom inte fram",
    "The money is still with Stripe and is not lost. Check your bank details.": "Pengarna ligger kvar hos Stripe och försvinner inte. Kontrollera dina bankuppgifter.",
    "Open Earnings": "Öppna Earnings",
    "counter-offers waiting for your reply": "motbud väntar på ditt svar",
    "The client has proposed a different price or set-up.": "Kunden har föreslagit ett annat pris eller upplägg.",
    "Open offers": "Öppna offerterna",
    "gig matches your profile today": "gig matchar din profil idag",
    "gigs match your profile today": "gigs matchar din profil idag",
    "Be quick — talents who reply first usually win the booking.": "Var snabb — talanger som svarar först får oftast bokningen.",
    "View gigs": "Visa gigs",
    "No new gigs right now": "Inga nya gigs just nu",
    "We will let you know as soon as an event in your category appears.": "Vi hör av oss så fort ett event i din kategori dyker upp.",
    "Update profile": "Uppdatera profil",
    "Revenue last 30d": "Intäkter senaste 30d",
    "vs previous": "vs föreg.",
    "New measurement": "Ny mätning",
    "Confirmed bookings": "Bekräftade bokningar",
    "last 30d": "senaste 30d",
    "Accept rate (90d)": "Accept-rate (90d)",
    "Good position": "Bra läge",
    "Room to respond more": "Utrymme att svara mer",
    "Average rating": "Snittbetyg",
    reviews_lc: "recensioner",
    "No reviews yet": "Inga recensioner än",
    "Upcoming booking": "Kommande bokning",
    "All bookings": "Alla bokningar",
    guests_lc: "gäster", days_lc: "dagar",
    Today: "Idag", Tomorrow: "I morgon", In: "Om",
    "item left in your checklist": "punkt kvar i din checklista",
    "items left in your checklist": "punkter kvar i din checklista",
    "No upcoming bookings yet.": "Inga kommande bokningar än.",
    "See available gigs": "Se tillgängliga gigs",
    Payout: "Utbetalning", incoming: "på väg in",
    "active offers": "aktiva offerter",
    Unlocks: "Upplåsta", "gigs unlocked": "gigs olåsta",
    "credits left": "credits kvar",
    "Recent activity": "Senaste aktivitet",
    "Nothing new right now.": "Inget nytt just nu.",
    "Browse gigs": "Bläddra gigs",
    "Update calendar": "Uppdatera kalender",
    "Upload media": "Ladda upp media",
    "Contact support": "Kontakta support",
    "Quick actions": "Snabbval",
    "Profile health": "Profil-hälsa",
    "Profile complete — well done.": "Profilen är komplett — bra jobbat.",
    "Almost there. Fill in the rest to appear in more searches.": "Nästan där. Fyll i resten för att synas i fler sökningar.",
    "Complete your profile to start receiving gigs.": "Fyll i profilen för att börja få gigs.",
    "Complete profile": "Fyll i profilen",
    "You need credits to unlock gigs.": "Du behöver credits för att låsa upp gigs.",
    "Enough to unlock more gigs.": "Räcker till att låsa upp fler gigs.",
    "Buy more": "Köp fler",
    // Notifications page.
    Notifications: "Notiser",
    "Requires action": "Kräver åtgärd",
    "Could not load notifications.": "Kunde inte hämta notiserna.",
    "Everything is marked as read.": "Allt är markerat som läst.",
    "Could not mark as read.": "Gick inte att markera som läst.",
    "item needs your attention.": "sak kräver något av dig.",
    "items need your attention.": "saker kräver något av dig.",
    "Everything is read.": "Allt är läst.",
    unread_lc: "olästa",
    "Mark all as read": "Markera allt som läst",
    Unread: "Olästa",
    "This week": "Senaste veckan",
    All: "Allt",
    "Nothing needs your attention.": "Ingenting kräver något av dig.",
    "No notifications here.": "Inga notiser här.",
    "We will get in touch when something happens with your bookings.": "Vi hör av oss när något händer med dina bokningar.",
    "This list only shows things that stay stuck if you do nothing: unanswered counter-offers, bounced payouts, overdue invoices and missing tax details.": "Här ligger bara sådant som stannar upp om du inte gör något: obesvarade motbud, studsade utbetalningar, förfallna fakturor och saknade skatteuppgifter.",
    "A notification is marked as read when you open it. The bell in the top bar only shows unread items.": "En notis markeras som läst när du öppnar den. Klockan i toppen visar bara olästa.",
    // Talent gigs page.
    "Open gigs": "Öppna gigs",
    "Refreshing…": "Uppdaterar…",
    "Release to refresh": "Släpp för att uppdatera",
    "Pull to refresh": "Dra för att uppdatera",
    "Updated just now": "Uppdaterat nyss",
    "open in your match": "öppna i din matchning",
    credits_lc: "credits", credit_lc: "credit",
    "Buy credits": "Köp credits",
    Filter: "Filter",
    "All open": "Alla öppna", Alerts: "Bevakade", Unlocked: "Unlockade", Hidden: "Dolda",
    Sort: "Sortera",
    "Best match": "Bäst match", Newest: "Nyast",
    "Highest budget": "Högst budget", "Soonest date": "Snart datum",
    "Matches your specialty": "Matchar din inriktning",
    "Matches your category": "Matchar din kategori",
    Unlocked_short: "Upplåst",
    "Fully booked": "Fullbokat",
    "Budget unknown": "Budget okänd",
    Budget: "Budget",
    "verified client": "verifierad kund",
    unlocked_lc: "unlockat",
    "you have unlocked": "ni har unlockat",
    Unlock: "Unlock",
    "View detail": "Visa detalj",
    Hide: "Göm",
    // Backend-computed notification & event labels (canonicalised to English on
    // the API side; translated here so SV users still see native strings).
    Requests: "Förfrågningar",
    Prep: "Inför eventet",
    System: "System",
    Yesterday: "I går",
    Older: "Äldre",
    "Not published": "Ej publicerad",
    "Searching for talents": "Söker talanger",
    "Offers to review": "Offerter att välja mellan",
    Booked: "Bokad",
    "Coming up soon": "Snart dags",
    Booking: "Bokning",
    Offer: "Offert",
    Upcoming: "Kommande",
    After: "Efter",

    // ─── Settings sidebar (talent) ─────────────────────────────────────────
    "Notifications & security": "Notiser & säkerhet",
    Data: "Data",
    "Personal info": "Personlig info",
    Payouts: "Utbetalningar",
    "Tax & DAC7": "Skatt & DAC7",
    "Notification channels": "Notis-kanaler",
    "Security & 2FA": "Säkerhet & 2FA",
    "GDPR & privacy": "GDPR & privacy",
    "Export my data": "Exportera min data",
    Preferences: "Preferenser",
    "Danger zone": "Danger zone",

    // ─── Notification channels page ────────────────────────────────────────
    "New gig request": "Ny gig-förfrågan",
    "Offer accepted": "Offert accepterad",
    "Payout sent": "Payout skickad",
    "New review": "Ny review",
    "Marketing & tips": "Marknad & tips",
    "In-app": "In-app",
    Push: "Push",
    Event: "Händelse",
    "Notification preferences saved": "Notis-inställningar sparade",
    "What you get notified about, and where": "Vad du får notis om, och var",
    "Pick which events you want to follow and in which channels. Critical notifications (payouts, booking confirmations) are always emailed even if you turn them off here.":
      "Välj vilka händelser du vill följa och i vilka kanaler. Kritiska notiser (payouts, bokningsbekräftelser) skickas alltid via mejl även om du stänger av dem här.",

    // ─── Security & 2FA page ───────────────────────────────────────────────
    Password: "Lösenord",
    "Change your password regularly. Use at least 12 characters — ideally a memorable passphrase.":
      "Byt lösenord regelbundet. Använd minst 12 tecken, gärna en fras du kan komma ihåg.",
    "Two-factor authentication (2FA)": "Tvåfaktors-autentisering (2FA)",
    "Protect your account with a one-time code from your authenticator app.":
      "Skydda kontot med en engångskod från din autentiseringsapp.",
    "2FA is not enabled": "2FA är inte aktiverat",
    "Turn it on for extra protection — requires Google Authenticator or similar.":
      "Aktivera för extra skydd — kräver Google Authenticator eller liknande.",
    "Enable 2FA": "Aktivera 2FA",
    "The 2FA flow ships in the next release. We'll email you the moment it's ready.":
      "2FA-flödet byggs i nästa release. Du får ett mejl så fort det är klart.",

    // ─── Sessions page ─────────────────────────────────────────────────────
    Sessions: "Sessioner",
    "Devices logged in": "Enheter som är inloggade",
    "Every device that currently has access to your account. Don't recognise one — sign it out immediately.":
      "Här ser du alla enheter som just nu har åtkomst till ditt kontot. Känner du inte igen någon — logga ut den direkt.",
    Current: "Aktuell",
    Now: "Nu",
    "2h ago": "för 2h sedan",
    "Session management ships with a \"sign out everywhere\" button in the next release.":
      "Session-hanteringen kompletteras med \"Logga ut på alla enheter\" i nästa release.",

    // ─── GDPR & privacy page ───────────────────────────────────────────────
    "What we store and why": "Vad vi lagrar och varför",
    "Account details": "Kontouppgifter",
    "name, email, personal ID number. Stored as long as the account is active.":
      "namn, mejl, personnummer. Lagras så länge kontot är aktivt.",
    "Financial info": "Ekonomiska uppgifter",
    "payouts, invoices, DAC7. Retained 7 years per Swedish bookkeeping law.":
      "utbetalningar, fakturor, DAC7. Lagras 7 år enligt bokföringslagen.",
    "Booking history": "Bokningshistorik",
    "offers, messages, reviews. Anonymised 90 days after account closure.":
      "bud, meddelanden, recensioner. Anonymiseras 90 dagar efter avslutat konto.",
    Consents: "Samtycken",
    "You can change what Talentsio may do with your data at any time.":
      "Du kan när som helst ändra vad Talentsio får göra med dina data.",
    "Processing of personal data to run the platform":
      "Behandling av personuppgifter för att kunna driva plattformen",
    "Anonymous analytics to improve the platform": "Anonym analys för att förbättra plattformen",
    "Marketing and newsletters": "Marknadsföring och nyhetsbrev",
    "Required for the account to work.": "Krävs för att kontot ska fungera.",
    "This consent is required for the account to work.":
      "Detta samtycke krävs för att kontot ska fungera.",

    // ─── Export my data page ───────────────────────────────────────────────
    "Download everything we have about you": "Ladda ner allt vi har om dig",
    "Under GDPR you have the right to a copy of every piece of data we store on you. We package it as a ZIP and email you a download link.":
      "Enligt GDPR har du rätt till en kopia av all data vi lagrat om dig. Vi paketerar den som en ZIP och skickar en nedladdningslänk till din registrerade mejl.",
    "The file includes:": "Filen innehåller:",
    "Profile information, images and media": "Profilinformation, bilder och media",
    "Booking history, offers and messages": "Bokningshistorik, offerter och meddelanden",
    "Invoices and payouts": "Fakturor och utbetalningar",
    "Reviews given and received": "Recensioner du gett och tagit emot",
    "Request export": "Begär export",
    "Sending request…": "Skickar begäran…",
    "We'll send a link to your email within 48 hours.":
      "Vi skickar en länk till din mejl inom 48 timmar.",

    // ─── Preferences page ──────────────────────────────────────────────────
    Interface: "Interface",
    "Small tweaks that make Talentsio yours.": "Små justeringar som gör Talentsio till din.",
    "Quiet mode on weekends": "Tysta läge under helger",
    "No push notifications Saturday–Sunday (emails still arrive).":
      "Inga push-notiser lördag–söndag (mejl kommer fortfarande fram).",
    "Auto-reply for gigs outside availability": "Auto-svar på gigs utanför tillgänglighet",
    "Automatically send a polite \"unfortunately busy\" reply.":
      "Skicka artigt \"tyvärr upptagen\"-svar automatiskt.",
    "Dark theme": "Mörkt tema",
    "Talentsio is always dark — a light theme will come later.":
      "Talentsio är alltid mörk — ljust tema kommer senare.",
    "Light theme is on the roadmap.": "Ljust tema är på roadmap.",

    // ─── Danger zone page ──────────────────────────────────────────────────
    "Delete my account": "Radera mitt konto",
    "When you delete your account you lose access to all your bookings, offers, messages and reviews. Financial data (invoices, DAC7) is retained for 7 years per Swedish bookkeeping law. This cannot be undone.":
      "När du raderar kontot förlorar du åtkomst till alla dina bokningar, offerter, meddelanden och recensioner. Ekonomiska uppgifter (fakturor, DAC7) behåller vi 7 år enligt bokföringslagen. Detta kan inte ångras.",
    "to confirm": "för att bekräfta",
    "Type DELETE exactly to confirm.": "Skriv exakt DELETE för att bekräfta.",
    "Deleting…": "Raderar…",
    "Delete account permanently": "Radera kontot permanent",
    "Your account has been marked for deletion.": "Kontot är markerat för radering.",
    "Could not delete the account.": "Kunde inte radera kontot.",

    // ─── Earnings page ─────────────────────────────────────────────────────
    "Next payout": "Nästa utbetalning",
    "At Stripe": "Hos Stripe",
    "Paid out": "Utbetalt",
    "Talentsio fee": "Talentsios avgift",
    "Nothing scheduled": "Inget inplanerat",
    "No comparison yet": "Ingen jämförelse än",
    "The money sits at your Stripe account for": "Pengarna ligger på ditt Stripe-konto i",
    "days after the customer paid, then it goes to your bank.":
      "dagar efter att kunden betalat, sedan går de till din bank.",
    "vs previous period": "mot förra perioden",
    "Plus": "Plus",
    "VAT, paid by the customer": "moms, betald av kunden",
    "The fee is added on top of your price and paid by the customer. It's never deducted from your fee.":
      "Avgiften läggs ovanpå ditt pris och betalas av kunden. Den dras aldrig från ditt gage.",
    "This year": "I år",
    "12 months": "12 månader",
    "All time": "Allt",
    Refunded: "Återbetalt",
    "Search customer or event": "Sök kund eller event",
    "No earnings in this period": "Inga intäkter i den här perioden",
    "When a customer pays for a booking it shows up here, with exactly what you get and when.":
      "När en kund betalar för en bokning dyker den upp här, med exakt vad du får och när.",
    "Find gigs": "Hitta gigs",
    "You get": "Du får",
    Estimated: "Beräknad",
    "We report your earnings to the Swedish Tax Agency once a year under DAC7 — we do it for you. Your status and paperwork live under Tax & reporting.":
      "Vi rapporterar dina intäkter till Skatteverket en gång om året enligt DAC7 och gör det åt dig. Din status och ditt underlag ligger under Skatt och rapportering.",
    "My invoices": "Mina fakturor",
    "Payout settings": "Utbetalningsinställningar",
    "Payout runs": "Utbetalning sker",
    "days after the customer paid, not after the event. So a booking paid far in advance pays out before the event.":
      "dagar efter att kunden betalat, inte efter eventet. En bokning som betalas långt i förväg betalas därför ut före eventet.",
    "Could not fetch your earnings.": "Kunde inte hämta dina intäkter.",
    "Could not open Stripe right now.": "Kunde inte öppna Stripe just nu.",

    // ─── Earnings — payout status banner ───────────────────────────────────
    "The payout didn't arrive": "Utbetalningen kom inte fram",
    "The money is still on your Stripe account and isn't going anywhere.":
      "Pengarna ligger kvar på ditt Stripe-konto och försvinner inte.",
    "Further payouts are paused until the details are fixed.":
      "Fler utbetalningar pausas tills uppgifterna är rättade.",
    "Fix bank details": "Rätta bankuppgifterna",
    "Connect your bank account to get paid": "Koppla ditt bankkonto för att få betalt",
    "You can accept bookings already, but payouts can't run until the account is connected.":
      "Du kan ta emot bokningar redan nu, men pengarna kan inte betalas ut förrän kontot är kopplat.",
    "Connect account": "Koppla konto",
    "Stripe needs 1 more detail": "Stripe behöver 1 uppgift till",
    "Stripe needs": "Stripe behöver",
    "more details": "uppgifter till",
    "Payouts are paused until the details are in place. Your money is safe — it's sitting at Stripe.":
      "Utbetalningarna är pausade tills uppgifterna är på plats. Pengarna är kvar, de ligger kvar hos Stripe.",
    "Complete now": "Komplettera",
    "Stripe is reviewing your account": "Stripe granskar ditt konto",
    "This usually takes under a day. You don't need to do anything — bookings still work in the meantime.":
      "Det tar oftast under ett dygn. Du behöver inte göra något, och bokningar fungerar under tiden.",
    "Payouts are active": "Utbetalningar är aktiva",
    "your account": "ditt konto",
    "ending in": "som slutar på",
    "Manage at Stripe": "Hantera hos Stripe",

    // ─── Earnings — transaction drawer / STATE labels ──────────────────────
    "Awaiting payment": "Väntar på betalning",
    "Payment started": "Betalning påbörjad",
    "Refund pending": "Återbetalning pågår",
    "The money": "Pengarna",
    "Customer paid": "Kunden betalade",
    "VAT on the fee": "Moms på avgiften",
    "The fee is on the customer and is never deducted from your fee. It's shown here so you can reconcile against what the customer sees on their invoice.":
      "Avgiften ligger på kunden och dras aldrig från ditt gage. Den syns här för att du ska kunna stämma av mot vad kunden ser på sin faktura.",
    Timeline: "Tidslinje",
    "Not yet": "Inte än",
    "At Stripe for": "Hos Stripe i",
    "In progress or done": "Pågår eller klart",
    Waiting: "Väntar",
    "Paid out to your bank": "Utbetald till din bank",
    "Not set": "Inte satt",
    "The payout falls before the event. That's expected: the 14 days run from when the customer paid, not from the event date.":
      "Utbetalningen ligger före eventet. Det är väntat: de 14 dagarna räknas från att kunden betalade, inte från eventdatumet.",
    Payment: "Betalning",

    // ─── Tax settings — additional strings ─────────────────────────────────
    "Talentsio, DAC7 report": "Talentsio, DAC7-underlag",
    Quarter: "Kvartal",
    "Amount SEK": "Ersättning kr",
    "Platform fee SEK": "Plattformsavgift kr",

    // ─── Client events / bookings pages ────────────────────────────────────
    "My events": "Mina events",
    "Could not load your events.": "Kunde inte hämta dina events.",
    "You have no events yet": "Du har inga events än",
    "Describe your event and you'll get offers from artists who are free. It costs nothing to ask.":
      "Beskriv ditt event så får du offerter från artister som är lediga. Det kostar ingenting att fråga.",
    "Describe your event": "Beskriv ditt event",
    Ongoing: "Pågående",
    Finished: "Avslutade",
    "event is waiting": "event väntar",
    "events are waiting": "events väntar",
    "for you to pick an offer.": "på att du väljer offert.",
    "An event is the same evening from request to review.":
      "Ett event är samma kväll från förfrågan till omdöme.",
    "No events in this view.": "Inga events i den här vyn.",
    guests: "gäster",
    "talent interested": "talang intresserad",
    "talents interested": "talanger intresserade",
    "Nothing here": "Ingenting här",
    "You have no upcoming bookings.": "Du har inga kommande bokningar.",
    "No completed bookings yet.": "Inga genomförda bokningar än.",
    "No cancelled bookings.": "Inga avbokade bokningar.",

    // ─── Client dashboard, offer/talent page ───────────────────────────────
    "Back to gig": "Tillbaka till gig",
    "Open gig": "Öppna gig",
    "offer waiting": "offert väntar",
    "offers waiting": "offerter väntar",
    "for a reply": "på svar",
    "Read them and pick the one you want to book. The offer price is the total price.":
      "Läs dem och välj den du vill boka. Priset i offerten är totalpriset.",
    "Read the offers": "Läs offerterna",
    "Payment isn't complete yet": "Betalningen är inte genomförd än",
    "The booking is confirmed, but the money isn't secured until payment goes through.":
      "Bokningen är bekräftad, men pengarna är inte säkrade förrän betalningen är klar.",
    "Complete the payment": "Slutför betalningen",
    "Share your playlist with": "Dela din spellista med",
    "the artist": "artisten",
    "The artist prepares the evening better with your wishes. Spotify, Apple Music or a text list all work equally well.":
      "Artisten förbereder kvällen bättre med dina önskemål. Spotify, Apple Music eller en textlista fungerar lika bra.",
    "Share playlist": "Dela spellista",
    In: "Om",
    Tomorrow: "I morgon",
    "You have no bookings yet": "Du har inga bokningar än",
    "Next event": "Nästa event",
    "Your artist": "Din artist",
    Artist: "Artist",
    "Message the artist": "Meddela artisten",
    "Open the booking": "Öppna bokningen",
    "Bankgiro, OCR and paperwork for your bookkeeping.":
      "Bankgiro, OCR och underlag för din bokföring.",

    // ─── Backend detail strings (from FastAPI HTTPException / ValueError) ──
    // The backend now returns canonical English; SV users see native strings
    // through this table. Keep in sync with backend routes when new detail
    // messages are added.
    "Could not load the refunds.": "Återbetalningarna gick inte att hämta.",
    "The refund does not exist.": "Återbetalningen finns inte.",
    "The request does not exist": "Förfrågan finns inte",
    "Unlock the gig to view the attachments": "Lås upp giget för att se bilagorna",
    "You have unlocked the gig and can no longer hide it.":
      "Du har låst upp giget och kan inte gömma det.",
    "A package must have a name": "Paket måste ha ett namn",
    "Only one package can be marked as most popular":
      "Bara ett paket kan vara markerat som populärast",
    "Hourly rate must be greater than zero": "Timpris måste vara högre än noll",
    "Minimum number of hours is 1": "Minsta antal timmar är 1",
    "Discount must be between 0 and 90 percent":
      "Rabatt måste vara mellan 0 och 90 procent",
    "Tax residency must be a country code, e.g. SE":
      "Skatterättslig hemvist anges som landskod, till exempel SE",
    "Describe what should change.": "Beskriv vad som ska ändras.",
    "Every line needs a name.": "Varje rad behöver ett namn.",
    "phase must be a number": "phase måste vara ett tal",
    "The artist hasn't proposed a time yet.": "Artisten har inte föreslagit någon tid än.",
    "Paste the full link — it must start with https.":
      "Klistra in hela länken, den ska börja med https.",
    "The link is too long.": "Länken är för lång.",
    "Only the parties can attach files to the booking.":
      "Bara parterna kan lägga filer på bokningen.",
    "Unknown file purpose.": "Okänt filsyfte.",
    "You can only attach files you uploaded yourself.":
      "Du kan bara koppla filer du själv laddat upp.",
    "The file is not on the booking.": "Filen finns inte på bokningen.",
    "Card payments are not enabled yet.": "Kortbetalningar är inte påslagna än.",
    "Stripe did not respond. Please try again.": "Stripe svarade inte. Försök igen.",
    "Could not load your cards right now.": "Korten gick inte att hämta just nu.",
    "Could not select that card.": "Det gick inte att välja kortet.",
    "Please state when you're on site.": "Ange när du är på plats.",
    "Please give the name and phone number of the on-site contact.":
      "Skriv namn och telefonnummer till den som finns på plats.",
    "Include a phone number — otherwise the contact can't be reached.":
      "Ta med ett telefonnummer, annars går personen inte att nå.",
    "That step isn't saved here": "Det steget sparas inte här",
    "Only the client or the talent can request a change.":
      "Bara kunden eller talangen kan begära en ändring.",
    "The request cannot be withdrawn.": "Förfrågan kan inte dras tillbaka.",
    "Only the counterparty can respond to the request.":
      "Bara motparten kan svara på förfrågan.",
    "The event has passed — the request no longer applies.":
      "Eventet har passerat. Förfrågan gäller inte längre.",
    "The amount no longer matches. Please ask for a new request.":
      "Beloppet stämmer inte längre. Be om en ny förfrågan.",
    "Payments are not configured.": "Betalningar är inte konfigurerade.",
    "Unlock the gig first.": "Lås upp giget först.",
    "The writing assistant is not configured.": "Skrivhjälpen är inte konfigurerad.",
    "The writing assistant did not respond. Please try again.":
      "Skrivhjälpen svarade inte. Försök igen.",
    "The attachments belong to the client's request and cannot be modified. Attach your own files inside your offer instead.":
      "Bilagorna är kundens underlag och går inte att ändra. Bifoga egna filer i din offert i stället.",

    // ─── 404 page ──────────────────────────────────────────────────────────
    "Page not found": "Sidan finns inte",
    "The link may be broken or the page has moved. Here are the ways back.":
      "Länken kan vara trasig eller sidan flyttad. Här är vägarna tillbaka.",
    "Go to my gigs": "Till mina gigs",
    "Go to overview": "Till översikten",
    "Go to my roster": "Till min roster",
    "Go to my bookings": "Till mina bokningar",
    "Go to my offers": "Till mina offerter",
    "Go to the to-do queue": "Till att göra",
    "Go to home": "Till startsidan",
    "Post a booking request": "Lägg upp en förfrågan",

    // ─── Credit history page ───────────────────────────────────────────────
    "Credit history": "Kredithistorik",
    "Back to credits": "Tillbaka till krediter",
    Purchase: "Köp",
    "Welcome credits": "Välkomstkrediter",
    Refund: "Återbetalning",
    "Auto top-up": "Automatisk påfyllning",
    Purchases: "Köp",
    Unlocks: "Upplåsningar",
    Refunds: "Återbetalningar",
    Date: "Datum",
    Type: "Typ",
    Credits: "Krediter",
    "Balance after": "Saldo efter",
    "No transactions.": "Inga transaktioner.",
    "Download receipt": "Ladda ner kvitto",
    Previous: "Föregående",
    Next: "Nästa",
    Page: "Sida",

    // ─── Onboarding, Event detail, Credits — Batch 3 ───────────────────────
    "Get started": "Kom i gång",
    "Could not load your status": "Kunde inte hämta ditt läge",
    "Your profile is live.": "Profilen är publicerad.",
    "Continue later": "Fortsätt senare",
    "What do you do?": "Vad gör du?",
    "Pick one or more. You can change this later.":
      "Välj en eller flera. Du kan ändra senare.",
    "What are you best at?": "Vad är du bäst på?",
    "Requests that match a specialty rise to the top of your feed.":
      "Förfrågningar som matchar en specialitet hamnar högst i ditt flöde.",
    "Where are you based?": "Var utgår du ifrån?",
    "Two sentences about what you do and what makes you the right choice.":
      "Två meningar om vad du gör och vad som gör dig till rätt val.",
    "This is what the client reads first when comparing offers.":
      "Det här är det kunden läser först när hon jämför offerter.",
    "Based on": "Räknat på",
    "profiles.": "profiler.",
    "How do you want to price?": "Hur vill du prissätta?",
    "Hourly rate": "Timpris",
    "Easiest to compare, and easy to recalculate for longer evenings.":
      "Enklast att jämföra, och lätt att räkna om för längre kvällar.",
    "Fixed packages": "Fasta paket",
    "You decide what's included. Good when jobs are similar.":
      "Du bestämmer vad som ingår. Bra när uppdragen liknar varandra.",
    "Offer on request": "Offert varje gång",
    "No price shown. You quote each request individually.":
      "Inget pris visas. Du räknar på varje förfrågan för sig.",
    "Excluding VAT. Talentsio's fee is added on top and paid by the client,":
      "Exklusive moms. Talentsios avgift läggs ovanpå och betalas av kunden,",
    "so this is what you get.": "så det här är vad du får.",
    "No price shows on your profile. You see the request's budget before you reply,":
      "Inget pris visas på din profil. Du ser förfrågans budget innan du svarar,",
    "and you set the price inside the offer.": "och sätter priset i offerten.",
    "Payouts connected": "Utbetalningar är kopplade",
    "Connect your bank account": "Koppla ditt bankkonto",
    "The money goes directly from the client to you. Talentsio never holds it.":
      "Pengarna går direkt från kunden till dig. Talentsio håller dem aldrig.",
    "Payout happens fourteen days after the event is completed.":
      "Utbetalningen sker fjorton dagar efter att eventet är genomfört.",
    "Open settings": "Öppna inställningarna",
    "Do this later": "Gör det senare",
    "You can send offers without this. The bank account is only needed once your":
      "Du kan skicka offerter utan det här. Bankkontot behövs först när din",
    "first payout is ready to send — i.e. after a client says yes.":
      "första utbetalning ska gå iväg, alltså efter att en kund tackat ja.",
    "Your profile is live": "Din profil är live",
    "You now show up for clients searching your category, and requests that":
      "Du syns nu för kunder som söker i din kategori, och förfrågningar som",
    "match appear in your feed.": "matchar dyker upp i ditt flöde.",
    "Worth doing next": "Värt att göra härnäst",
    "See requests": "Se förfrågningar",
    "This gets published": "Det här publiceras",
    "Something's missing:": "Det saknas något:",
    Continue: "Fortsätt",

    // Event detail
    "Lowest price": "Lägst pris",
    "total, all inclusive": "totalt, allt inräknat",
    "Read the full offer": "Läs hela offerten",
    "Ask a question first": "Fråga något först",
    "Valid until": "Gäller till",
    "Could not open the chat.": "Kunde inte öppna chatten.",
    "The talent has been asked to send an offer.": "Talangen har fått en förfrågan om att skicka en offert.",
    "Could not send the request.": "Kunde inte skicka förfrågan.",
    "Request offer": "Begär offert",
    "Talents who unlocked your event show up here. Send them a direct message, or":
      "Talanger som låst upp ditt event visas här. Skicka ett meddelande direkt, eller",
    "ask one for a formal bid.": "be någon om ett formellt bud.",
    "Could not load the event.": "Kunde inte hämta eventet.",
    "It has been removed, or it belongs to another account.":
      "Det är borttaget, eller så tillhör det ett annat konto.",
    offer: "offert",
    offers: "offerter",
    "to choose from": "att välja mellan",
    "The offer price is the total. Nothing is added at checkout, and the amount":
      "Priset i offerten är totalpriset. Det tillkommer ingenting i kassan, och beloppet",
    "does not change after you accept.": "ändras inte efter att du accepterat.",
    "Your request is out": "Din förfrågan är ute",
    "Talents in your category see it now. Most replies come within a day, and you get":
      "Talanger i din kategori ser den nu. De flesta svar kommer inom ett dygn, och du får",
    "email the moment an offer lands. You don't need to do anything in the meantime.":
      "mejl så fort en offert landar. Du behöver inte göra något under tiden.",
    "This is agreed": "Det här är avtalat",
    "Artist on site": "Artisten på plats",
    "Included in the price": "Ingår i priset",
    "What happened": "Vad som hänt",
    "On site": "På plats",
    "The payment is handled by Stripe and held until after the event. If the artist cancels":
      "Betalningen hanteras av Stripe och hålls till efter eventet. Ställer artisten in",
    "you get the full amount back, Talentsio's fee included.":
      "får du hela beloppet tillbaka, Talentsios avgift inkluderad.",
    "Open the list": "Öppna listan",
    Actions: "Åtgärder",
    "Request a change": "Begär en ändring",
    "Report a problem": "Anmäl ett problem",
    "A cancellation follows the terms below. The refund amount is shown before you":
      "En avbokning följer villkoren nedan. Beloppet som återbetalas visas innan du",
    "confirm.": "bekräftar.",
    Talent: "Talang",
    hours: "timmar",
    "Interested talents": "Talanger intresserade",
    talent: "talang",
    talents: "talanger",
    "Bid sent": "Bud skickat",
    "Send message": "Skicka meddelande",
    Message: "Meddelande",

    // Credits
    "Payment status pending.": "Betalningsstatus väntar.",
    "credits added to your balance!": "krediter har lagts till!",
    "Payment session expired.": "Betalningssessionen gick ut.",
    "Confirming payment...": "Bekräftar betalning...",
    "Could not start checkout": "Kunde inte starta betalning",
    "Auto top-up settings saved": "Inställningar sparade",
    "Ready to unlock your first lead?": "Redo att låsa upp din första förfrågan?",
    "Running low? Top up to never miss a lead":
      "Ont om krediter? Fyll på för att aldrig missa en förfrågan",
    "Credits never expire": "Krediter går aldrig ut",
    "Payment method": "Betalmetod",
    ending: "slutar på",
    Change: "Ändra",
    "Buy credits": "Köp krediter",
    "Opening...": "Öppnar...",
    "Buy now": "Köp nu",
    "Auto top-up": "Automatisk påfyllning",
    "Refill automatically when your balance runs low":
      "Fyll på automatiskt när saldot blir lågt",
    "When your balance drops below": "När saldot sjunker under",
    "Automatically buy": "Köp automatiskt",
    "Using your saved payment method": "Med din sparade betalmetod",
    "No card saved, add one at your next purchase":
      "Inget kort sparat, lägg till vid ditt nästa köp",
    "You can turn this off anytime. We will email you before each auto-purchase.":
      "Du kan stänga av detta när som helst. Vi mejlar dig innan varje automatiskt köp.",
    "Saving...": "Sparar...",
    "Save auto top-up settings": "Spara inställningar",
    "Secure payment via Stripe": "Säker betalning via Stripe",
    "VAT invoice on every purchase": "Momsfaktura vid varje köp",
    "Dispute protection on bad leads": "Tvistskydd vid dåliga förfrågningar",
    "How credits work": "Så fungerar krediter",
    "Browse real booking requests from organisers":
      "Se riktiga bokningsförfrågningar från arrangörer",
    "Unlock a lead and assign your best roster talent to respond":
      "Lås upp en förfrågan och tilldela din bästa talang att svara",
    "Unlock a lead to reveal contact details and send your offer":
      "Lås upp en förfrågan för att se kontaktuppgifter och skicka din offert",
    "Get booked and keep 100% of your fee":
      "Bli bokad och behåll 100% av ditt arvode",
    "How much could you earn?": "Hur mycket kan du tjäna?",
    "gig/month": "gig/mån",
    "gigs/month": "gigs/mån",
    "for the same gig": "för samma gig",

    // Misc
    "YYYYMMDD-XXXX": "ÅÅMMDD-XXXX",
    "Self-billing (SV)": "Självfakturering",

    // ─── Batch 3 final: Billing, Media, Pricing, Invoices, InvoicePay ─────
    // ClientBilling
    "Billing details saved.": "Faktureringsuppgifterna är sparade.",
    "Check the highlighted fields.": "Kontrollera de markerade fälten.",
    "These details appear on the invoices you receive. If you leave them empty we use the":
      "Uppgifterna hamnar på fakturorna du får. Fyller du inte i dem används det",
    "fields left": "fält kvar",
    Individual: "Privatperson",
    Company: "Företag",
    "Company name": "Företagsnamn",
    "Invoice email address": "Fakturaadress för e-post",
    "Where the invoice is sent. If empty we use your normal email.":
      "Dit fakturan skickas. Lämnas den tom används din vanliga e-post.",
    "Your reference": "Er referens",
    "Cost centre, purchaser or PO number.": "Kostnadsställe, beställare eller inköpsordernummer.",
    "Card saved.": "Kortet är sparat.",
    "you confirm yourself.": "bekräftar du själv.",
    Default: "Standard",
    Expired: "Utgånget",
    "Expires soon": "Går ut snart",
    unknown: "okänt",
    "Cannot be used for payment.": "Går inte att betala med.",
    "Use as default": "Använd som standard",
    "Remove card": "Ta bort kortet",
    "Could not load your cards.": "Korten gick inte att hämta.",
    "Card removed.": "Kortet är borttaget.",
    "Card payments aren't enabled in this environment yet. You can still pay":
      "Kortbetalningar är inte påslagna i den här miljön än. Du kan fortfarande betala",
    "A saved card is faster at checkout. Nothing is charged without your confirmation.":
      "Ett sparat kort går snabbare i kassan. Ingenting dras utan att du bekräftar.",
    "Add card": "Lägg till kort",
    "All your saved cards have expired. Add a new one before your next booking,":
      "Alla dina sparade kort har gått ut. Lägg till ett nytt innan nästa bokning,",
    "A card expires soon.": "Ett kort går ut inom kort.",
    "cards expire soon.": "kort går ut inom kort.",
    "A card that expires between booking and payment blocks the payment.":
      "Ett kort som går ut mellan bokningen och betalningen stoppar betalningen.",
    "You have no saved cards. You can still pay by card at checkout every time.":
      "Du har inga sparade kort. Du kan fortfarande betala med kort i kassan varje gång.",
    "Could not load your billing details.": "Faktureringsuppgifterna gick inte att hämta.",
    "Payment is by card or invoice. We hold no money: the payment goes to":
      "Betalning sker med kort eller faktura. Vi håller inga pengar: betalningen går till",
    "the artist, and Talentsio's fee is added on top of the amount.":
      "artisten, och Talentsios avgift ligger ovanpå beloppet.",

    // TalentMedia
    "Could not load your media.": "Kunde inte hämta din media.",
    link: "länk",
    links: "länkar",
    "could not be saved.": "gick inte att spara.",
    "Your media has been saved.": "Din media är sparad.",
    "This is what sells you. A client who has read three profiles doesn't remember":
      "Det här är det som säljer dig. En kund som läst tre profiler minns inte",
    "the text, they remember the clip. Paste a link from YouTube, Vimeo, Spotify,":
      "texten, hen minns klippet. Klistra in en länk från YouTube, Vimeo, Spotify,",
    "You've reached the limit. Remove one before adding more. A profile with":
      "Du har nått taket. Ta bort något innan du lägger till mer. En profil med",
    "a hundred clips is no profile.": "hundra klipp är ingen profil.",
    "This could not be saved": "Det här gick inte att spara",
    "We only show media from players we recognise, and only over a secure":
      "Vi visar bara media från spelare vi känner igen, och bara över säker",
    "connection. A link we can't embed you can add as a normal link":
      "anslutning. En länk vi inte kan bädda in kan du lägga som en vanlig länk",
    "on the profile instead.": "på profilen i stället.",
    "One of the clips can be starred. It shows first and full-width on your":
      "Ett av klippen kan märkas med stjärnan. Det visas först och i full bredd på din",
    "profile — i.e. the first thing a client sees. Pick the one that works without sound and without":
      "profil, alltså det första en kund ser. Välj det som fungerar utan ljud och utan",
    "No clip is featured. Then they show in the order you added them.":
      "Inget klipp är utvalt. Då visas de i den ordning du lagt dem.",
    "The clips show publicly on your profile as soon as it's approved.":
      "Klippen visas publikt på din profil så snart den är godkänd.",

    // TalentPricing
    "You set 1 to 5 fixed packages. The client picks one and sees the price right away.":
      "Du sätter 1 till 5 fasta paket. Kunden väljer ett och ser priset direkt.",
    "Best for DJs and bands with clear formats.": "Bäst för DJ och band med tydliga upplägg.",
    "You set an hourly rate and a minimum number of hours. The price is calculated when the client picks a length.":
      "Du sätter ett timpris och minsta antal timmar. Priset räknas ut när kunden väljer längd.",
    "Best for musicians who play different lengths.": "Bäst för musiker som spelar olika länge.",
    "No price shown. The client sends a short request and you reply with an offer.":
      "Inget pris visas. Kunden skickar en kort förfrågan och du svarar med offert.",
    "Best when every job is unique.": "Bäst när varje jobb är unikt.",
    Packages: "Paket",
    "Could not load your pricing.": "Kunde inte hämta din prissättning.",
    "Pick how you want to price. Your public profile renders after your choice, and clients":
      "Välj hur du vill prissätta. Din publika profil renderas efter valet, och kunder",
    "who see a price send more requests.": "som ser ett pris skickar fler förfrågningar.",
    "Switching modes is non-destructive. Switching to hourly hides the packages but doesn't delete them,":
      "Byte av läge är inte destruktivt. Byter du till timpris göms paketen men raderas inte,",
    "and switching back leaves them exactly as you left them.":
      "och byter du tillbaka finns de kvar precis som du lämnade dem.",
    "Add package": "Lägg till paket",
    "No packages yet": "Inga paket än",
    "A package is a fixed setup with a fixed price. Most talents have three:":
      "Ett paket är ett fast upplägg med ett fast pris. De flesta talanger har tre:",
    "Create your first package": "Skapa ditt första paket",
    "Evening set": "Kvällspass",
    Price: "Pris",
    "Your price. Talentsio's fee is added on top and paid by the client.":
      "Ditt pris. Talentsios avgift läggs ovanpå och betalas av kunden.",
    "Max guests": "Max antal gäster",
    Optional: "Valfritt",
    "What's included": "Vad ingår",
    "One line is enough. The client sees it under the price.":
      "En rad räcker. Kunden ser den under priset.",
    "Own equipment included, up to 80 guests": "Egen utrustning ingår, upp till 80 gäster",
    "Discount from 2 hours": "Rabatt från 2 timmar",
    "Percent. Leave 0 if you don't give a discount.": "Procent. Lämna 0 om du inte ger rabatt.",
    "Discount from 3 hours": "Rabatt från 3 timmar",
    Percent: "Procent",
    "The client sees that you reply with an offer instead of a price. Good when every":
      "Kunden ser att du svarar med offert i stället för ett pris. Det passar när varje",
    "job is different, but be aware that profiles with a price get more requests.":
      "uppdrag är olika, men var medveten om att profiler med pris får fler förfrågningar.",
    "You can switch modes anytime.": "Du kan byta läge när som helst.",
    "Changes are visible on your public profile immediately.":
      "Ändringarna syns direkt på din publika profil.",
    "This is how it looks to the client": "Så här ser det ut för kunden",

    // ClientInvoices + InvoicePay
    Overdue: "Förfallen",
    Invoice: "Faktura",
    from: "från",
    Issued: "Utställd",
    due: "förfaller",
    "VAT 0 percent on artist fee under the Swedish VAT Act ch. 10 §31. Equipment and travel are invoiced":
      "Moms 0 procent på gage enligt momslagen 10 kap. 31 §. Utrustning och resa faktureras",
    "Reverse charge. As the purchaser you account for VAT in your own country.":
      "Omvänd skattskyldighet. Du som köpare redovisar momsen i ditt eget land.",
    "Open the pay page": "Öppna betalsidan",
    "View the invoice": "Se fakturan",
    "The artist hasn't set up any payment method. Contact them or support.":
      "Artisten har inte lagt in något betalsätt. Hör av dig till dem eller till supporten.",
    "If you pay by card it goes through the booking, not the invoice.":
      "Betalar du med kort går det via bokningen, inte via fakturan.",
    "The invoice is": "Fakturan är",
    "The status is set by the artist and by": "Statusen sätts av artisten och av",
    "the payment matching — it can't be changed from here.":
      "betalningsmatchningen, den går inte att ändra härifrån.",
    "Open the invoice": "Öppna fakturan",
    "Could not load your invoices.": "Kunde inte hämta dina fakturor.",
    "No invoices yet": "Inga fakturor än",
    "Invoices from your artists show up here when they're sent. You also get an email.":
      "Fakturor från dina artister dyker upp här när de skickas. Du får också ett mejl.",
    invoice: "faktura",
    invoices: "fakturor",
    "waiting for payment": "väntar på betalning",
    "The artist's invoice covers the artist's amount. Talentsio's service fee is a separate line and":
      "Artistens faktura gäller artistens belopp. Talentsios serviceavgift är en egen post och",
    "never appears on this invoice.": "ligger aldrig på den här fakturan.",
    "The invoice does not exist, or it's not yours.": "Fakturan finns inte, eller är inte din.",
    Unpaid: "Obetald",
    "Was due": "Förföll",
    day: "dag",
    "ago.": "sedan.",
    "Due today,": "Förfaller i dag,",
    "The artist only receives their payout after the invoice is paid.":
      "Artisten får sin utbetalning först efter att fakturan är betald.",
    "Artist fees are VAT-exempt under the Swedish VAT Act ch. 10 §31. This applies to the":
      "Artistgage är momsbefriat enligt momslagen 10 kap. 31 §. Det gäller själva",
    "performance itself, not equipment or travel.": "framförandet, inte utrustning eller resor.",
    "Reverse charge. You account for VAT in your own return.":
      "Omvänd skattskyldighet. Du redovisar momsen i din egen deklaration.",
    "The invoice is paid": "Fakturan är betald",
    "The artist receives their payout fourteen days after the event.":
      "Artisten får sin utbetalning fjorton dagar efter att eventet är genomfört.",
    "the invoice can't match automatically and may need to be sorted by hand.":
      "fakturan automatiskt, och den kan behöva redas ut för hand.",
    "The artist hasn't filled in a bankgiro. Get in touch and we'll sort the":
      "Artisten har inte fyllt i något bankgiro. Hör av dig till oss så löser vi",
    "payment and remind the artist.": "betalningen, och påminner artisten.",
    "Pay by card instead": "Betala med kort i stället",
    "Card payment sits on the booking, alongside the contract.":
      "Kortbetalningen ligger på bokningen, tillsammans med avtalet.",
    "Payment goes to the artist, who is the seller. The payout happens fourteen":
      "Betalningen går till artisten, som är säljare. Utbetalningen sker fjorton",
    "days after the event is completed.": "dagar efter att eventet är genomfört.",
    "An agency would take": "En agentur skulle ta",
    Invoices: "Fakturor",
    // Credits page — Swedish overrides matching the EN identity keys above.
    SEK: "kr",
    "currency.SEK": "kr",
    "Your credits": "Dina krediter",
    "incl. VAT": "inkl. moms",
    "per credit": "per kredit",
    "kr per credit": "kr per kredit",
    "Save %": "Spara {n}%",
    "Could not save settings": "Kunde inte spara",
    "View your credit history": "Visa din kredithistorik",
    "How much could you earn?": "Hur mycket kan du tjäna?",
    "Most popular": "Mest populär",
    "Best value": "Bäst värde",
    calc_sentence: "Om ditt genomsnittliga gig ger {avg} och du låser upp {leads} förfrågningar för att vinna {gigs} gig, är din kostnad {cost}, bara {pct}% av intäkten",
    // Spotlight index page
    "TALENTSIO SPOTLIGHT": "TALENTSIO SPOTLIGHT",
    "Ideas, trends and guides for people who book talent": "Idéer, trender och guider för dig som bokar",
    "Hand-picked articles for organisers, wedding couples and companies who want to book the right entertainment in Sweden.": "Handplockade artiklar för organisatörer, bröllopspar och företag som vill boka rätt underhållning i Sverige.",
    "No articles yet": "Inga artiklar ännu",
    "We're working on new stories - check back soon.": "Vi jobbar på nya stories – kom tillbaka snart.",
    "Read article": "Läs artikel",
    // Public Join page
    "Become a talent": "Bli en talang",
    "Get booked by organisers across Sweden": "Bli bokad av arrangörer i hela Sverige",
    "Create your free profile in under two minutes and start receiving matched requests.": "Skapa din gratis profil på under två minuter och börja ta emot matchade förfrågningar.",
    "3 free credits to get started": "3 gratis krediter för att komma igång",
    "Real booking requests with date and budget": "Riktiga bokningsförfrågningar med datum och budget",
    "Credits never expire, keep 100% of your fee": "Krediter går aldrig ut, behåll 100% av arvodet",
    join_pricing_line: "Börja med 3 gratis krediter, lås sedan upp förfrågningar från bara {per} {currency} styck",
    "Create your free profile": "Skapa din gratis profil",
    "No card needed. No monthly fees.": "Inget kort behövs. Inga månadsavgifter.",
    "Email address": "E-postadress",
    // BookNowButton
    "Book now": "Boka nu",
    // BookingDetail page title fallback
    Booking: "Bokning",
    // SpotlightArticle
    "Article not found": "Artikel hittades inte",
    "Perhaps it was unpublished or moved.": "Kanske är den avpublicerad eller flyttad.",
    "Back to Spotlight": "Tillbaka till Spotlight",
    "Link copied": "Länk kopierad",
    "All articles": "Alla artiklar",
    Share: "Dela",
    "Book the right entertainment on Talentsio": "Boka rätt underhållning på Talentsio",
    "Post a free request and get priced offers from verified artists directly.": "Skicka en gratis förfrågan och få prissatta bud från verifierade artister direkt.",
    "Post request": "Skicka förfrågan",
    // ForTalents public page
    "3 free credits when you complete your profile": "3 gratis krediter när du fyller i din profil",
    "Full VAT invoice on every purchase": "Fullständig momsfaktura vid varje köp",
    "Welcome bonus:": "Välkomstbonus:",
    "3 free unlocks worth 297 kr when you complete your profile.": "3 gratis upplåsningar värda 297 kr när du fyller i din profil.",
    "Simple, transparent pricing": "Enkel, transparent prissättning",
    "One flat price per unlock. No packages, no monthly fees. Keep what you invoice, minus a transparent 10% platform fee shown in every offer.": "Ett fast pris per upplåsning. Inga paket, inga månadsavgifter. Behåll det du fakturerar minus en transparent 10 % plattformsavgift som visas i varje offert.",
    "Per unlock": "Per upplåsning",
    "Same price for talents and agencies. Incl. VAT.": "Samma pris för talanger och byråer. Inkl. moms.",
    "Take a 10-pack for 799 kr and save nearly 20%.": "Ta ett 10-pack för 799 kr och spara nästan 20 %.",
    // ClientInvoices — panel + STATE labels
    Unpaid: "Att betala",
    "Partially paid": "Delbetald",
    Credited: "Krediterad",
    "To pay": "Att betala",
    "Pay against invoice": "Betala mot faktura",
    "Nothing to pay": "Ingenting att betala",
    "Copy {label}": "Kopiera {label}",
    "billed at their respective VAT rates.": "med respektive momssats.",
    "Could not save.": "Det gick inte att spara.",
    "Could not save": "Kunde inte spara",
    "Could not save card": "Kortet gick inte att spara.",
    "Could not remove card": "Kortet gick inte att ta bort.",
    "Send offer": "Skicka offert",
    "View public profile": "Visa publik profil",
    "No price shown": "Inget pris visas",
    Remove: "Ta bort",
    "Remove {label}?": "Ta bort {label}?",
    "Pay invoice": "Betala faktura",
    "Subtotal ex. VAT": "Summa exklusive moms",
    "Publish profile": "Publicera profilen",
  },
};

/**
 * Dashboard-scoped translator hook.
 * Returns a **stable** `dt(key)` function whose identity only changes when the
 * active language or the admin overrides map change. This is critical:
 * embedding `dt` in a `useEffect` or `useCallback` deps array MUST NOT trigger
 * refetch/render loops. (Historical bug: dt was re-created every render and
 * silently broke Checkout + Payment Settings pages with an infinite refetch.)
 */
export const useDashT = () => {
  const { lang, overrides } = useLang();
  return useCallback(
    (key) => overrides?.[lang]?.[`ds.${key}`] ?? STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key,
    [lang, overrides],
  );
};
